import type { ApiResponse, AuthTokens, JwtPayload, Site, SiteVersion, ChatMessage, AdminSettings, DashboardStats, SelectedElementContext, ImagePlacementContext, UploadedImage, ColorScheme } from '@siteforge/shared';
import { hasParkedAdminToken, parkedAdminRequest } from './parkedAdminToken';
import { shouldClearExpiredSession } from '../utils/sessionExpiry';
import { serverErrorMessage } from '../utils/serverErrorMessage';

const API_BASE = '/api';

let accessToken: string | null = localStorage.getItem('accessToken');
let refreshToken: string | null = localStorage.getItem('refreshToken');

/**
 * Persist a value to localStorage without ever throwing.
 *
 * A successful login must never be derailed by client-side storage limits.
 * Heavy editor use fills the per-origin quota with `sf.undo.*` / `sf.redo.*`
 * HTML snapshots (see usePersistentUndoHistory — worst case ~12 MB), so a
 * later `setItem('refreshToken', …)` can throw QuotaExceededError. That used
 * to propagate out of setTokens() → useAuth.login() and surface the raw
 * DOMException ("Failed to execute 'setItem' on 'Storage': Setting the value
 * of 'refreshToken' exceeded the quota.") on the /login screen — even though
 * the backend had already authenticated the user.
 *
 * On failure we evict the largest safely-disposable consumers (the persisted
 * undo/redo stacks, which only power "survive a refresh" undo) and retry once.
 * If it still fails we give up quietly: the token is already held in memory
 * for this session, so the user stays logged in. Returns whether it persisted.
 */
export function safeSetItem(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch {
    // Likely QuotaExceededError. Reclaim space from the biggest, throwaway
    // consumers — the per-project undo/redo HTML stacks — then retry once.
    try {
      const evictable: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('sf.undo.') || k.startsWith('sf.redo.'))) evictable.push(k);
      }
      evictable.forEach((k) => localStorage.removeItem(k));
      localStorage.setItem(key, value);
      return true;
    } catch {
      // Storage still unavailable (private mode, quota 0, or non-undo bloat).
      // The in-memory token below keeps this session signed in regardless.
      return false;
    }
  }
}

export function setTokens(tokens: { accessToken: string; refreshToken: string } | null) {
  if (tokens) {
    // Set in-memory first so the session is authenticated even if the
    // localStorage writes below fail (full quota / private mode). Login must
    // never hard-fail on a client-side storage limit.
    accessToken = tokens.accessToken;
    refreshToken = tokens.refreshToken;
    safeSetItem('accessToken', tokens.accessToken);
    safeSetItem('refreshToken', tokens.refreshToken);
  } else {
    accessToken = null;
    refreshToken = null;
    try {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } catch { /* storage unavailable — the in-memory clear above is enough */ }
  }
}

export function getAccessToken(): string | null {
  return accessToken;
}

// ── Single-flight token refresh ──
//
// The access token is short-lived (15m) and the editor fires several concurrent
// background requests (the inflight-runs poller every ~5s, edit heartbeats, plus
// whatever the user is doing). When the token crosses expiry, multiple of them
// 401 at the same instant. If each independently POSTs /auth/refresh, the server
// rotates the refresh token on the FIRST call (deleting the old one); the other
// in-flight refreshes then present the now-deleted token, get 401, and the user
// is silently logged out mid-session. Collapsing all concurrent refreshes into
// ONE shared promise means the rotation happens exactly once and every waiter
// reuses its result. Returns the fresh access token, or null if the session has
// genuinely ended.
let inflightRefresh: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (inflightRefresh) return inflightRefresh;
  const rt = refreshToken;
  if (!rt) {
    // #2189 (review round 2) — no refresh token AT ALL (e.g. an impersonation session
    // minted access-token-only — AdminClients.tsx:427's
    // `refreshToken: res.data.refreshToken || ''`) can never be refreshed, so an
    // unconditional clear+redirect is correct here — the original behavior, before every
    // caller's own handleSessionExpired() call was removed in favor of refreshOnce()
    // running it once, centrally. Passing null (not `rt`, which is already falsy and
    // useless as a comparison key) keeps this the unconditional path, same as
    // shouldClearExpiredSession's null case. Safe for the race the rest of this file
    // guards against: there is no await between reading `rt` above and this call, so
    // nothing concurrent can have set a newer session in the gap for this to wipe.
    handleSessionExpired(null);
    return Promise.resolve(null);
  }
  inflightRefresh = (async () => {
    try {
      const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (refreshRes.ok) {
        const refreshData = await refreshRes.json();
        if (refreshData.success && refreshData.data) {
          setTokens(refreshData.data);
          return refreshData.data.accessToken as string;
        }
      }
      // #2189 — pass the SPECIFIC token `rt` that just failed (not a blanket clear): see
      // handleSessionExpired's own comment for why this matters.
      handleSessionExpired(rt);
      return null;
    } catch {
      handleSessionExpired(rt);
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();
  return inflightRefresh;
}

// Public, no-auth routes — never bounce these to /login on a 401.
// #2189 — /resume is the re-engagement share-link landing: it signs the visitor in from
// its OWN URL token (ResumePage), independently of whatever stale/expired tokens already
// sit in localStorage. Without it here, a stale access token makes the boot-time
// getMe() -> refresh -> 401 chain call handleSessionExpired and hard-redirect to /login
// in a race against ResumePage's own token exchange — whichever finishes first wins, so a
// slow boot chain can bounce a logged-out clicker to /login before they're ever signed in
// (gotcha-token-login-page-spa-navigate-bounces-to-login-2026-09-28.md).
function isPublicPath(): boolean {
  const p = window.location.pathname;
  return p.startsWith('/preview') || p.startsWith('/outreach') ||
    p.startsWith('/claim') || p.startsWith('/share') || p.startsWith('/pay') ||
    p.startsWith('/resume');
}

// Surface a clear "your session ended" message instead of a silent redirect.
// Deduped so a burst of concurrent 401s redirects/notifies at most once.
//
// #2189 — `failedRefreshToken` is the refresh token that was being attempted when it
// failed. refreshOnce() is now the ONLY caller (it passes its own `rt`); every request
// helper that used to call this itself after refreshOnce() returned null now just reports
// its own request's failure — refreshOnce() already ran this, correctly gated, for all of
// them. The default null keeps this safely callable with no specific token to compare
// (an unconditional clear, the original behavior) if something ever needs to call it
// directly. A concurrent, successful token set — e.g. ResumePage's own
// /resume exchange finishing while this failing refresh was still in flight — already
// replaced the session that failed. Never wipe a NEWER, valid session just because an
// OLDER refresh attempt lost the race: only clear/redirect when the tokens that failed are
// still the ones currently in effect. Without this, a stale access+refresh token pair in
// localStorage could silently wipe the fresh tokens ResumePage (or any other token-from-URL
// landing page) had JUST applied, moments after they were set.
let sessionExpiredHandled = false;
function handleSessionExpired(failedRefreshToken: string | null = null) {
  if (!shouldClearExpiredSession(failedRefreshToken, refreshToken)) return;
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  setTokens(null);
  if (!isPublicPath()) {
    try { sessionStorage.setItem('sf.sessionExpired', '1'); } catch { /* storage unavailable */ }
    window.location.href = '/login';
  }
}

// Authed fetch with single-flight refresh — a drop-in for `fetch('/api'+path, opts)`
// for callers that need the RAW Response (e.g. admin pages that inspect/parse it
// themselves rather than the ApiResponse shape `request()` returns). On 401 it
// refreshes once (shared with the rest of api.ts) and retries; on genuine expiry
// it runs the session-expired flow. #1117 — admin pages used bare fetch with no
// refresh, so they silently failed on token expiry. `path` is relative to /api.
export async function authedFetch(path: string, options: RequestInit = {}): Promise<Response> {
  const withAuth = (token: string | null): RequestInit => ({
    ...options,
    headers: {
      ...(options.headers as Record<string, string> | undefined),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  let res = await fetch(`${API_BASE}${path}`, withAuth(accessToken));
  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    // #2189 — a falsy newAccess means refreshOnce() already ran the session-expired flow
    // itself (generationally gated — see handleSessionExpired); nothing further to do here.
    if (newAccess) {
      res = await fetch(`${API_BASE}${path}`, withAuth(newAccess));
    }
  }
  return res;
}

export async function request<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  let res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Handle token refresh (single-flight so concurrent 401s don't race the
  // server's refresh-token rotation and silently log the user out — see
  // refreshOnce/handleSessionExpired above).
  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      headers['Authorization'] = `Bearer ${newAccess}`;
      res = await fetch(`${API_BASE}${path}`, { ...options, headers });
    } else {
      // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
      // gated — see handleSessionExpired); this request still fails either way.
      throw new Error('Session expired');
    }
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return {
      success: false,
      error: `Server returned ${res.status}: unexpected response (not JSON). The backend may be down.`,
    } as ApiResponse<T>;
  }

  let data: any;
  try {
    data = await res.json();
  } catch {
    // The server claimed JSON (content-type checked above) but the body
    // didn't parse — a truncated response, a proxy/timeout cutting the
    // stream short, etc. Without this guard the raw browser SyntaxError
    // ("Unexpected end of JSON input" / "Unexpected token … is not valid
    // JSON") would escape uncaught to the caller and get shown to the user
    // as-is — meaningless to anyone who isn't reading the code. Every
    // caller of request() (including step-5 of reseller signup) gets a
    // clean, actionable message instead.
    return {
      success: false,
      error: `Server returned ${res.status}: the response was incomplete or malformed. Please try again in a moment.`,
    } as ApiResponse<T>;
  }
  if (!res.ok && !data.error) {
    data.error = `Request failed with status ${res.status}`;
  }
  return data;
}

// ── Branding API (public, no auth) ──

export interface BrandingData {
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
}

export async function getBranding(): Promise<ApiResponse<BrandingData>> {
  return request('/branding');
}

export async function uploadBrandingLogo(file: File): Promise<ApiResponse<{ url: string }>> {
  const formData = new FormData();
  formData.append('image', file);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}/admin/branding/upload-logo`, {
    method: 'POST',
    headers,
    body: formData,
  });

  return res.json();
}

// ── Auth API ──

export async function login(email: string, password: string): Promise<ApiResponse<AuthTokens & { user: JwtPayload }>> {
  return request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export interface SignupAttribution {
  signupSource?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  gbraid?: string;
  wbraid?: string;
  /** Meta Pixel `_fbp` cookie value — passed through for server-side Conversions API dedup. */
  fbp?: string;
  /** Meta Pixel `_fbc` cookie value — passed through for server-side Conversions API dedup. */
  fbc?: string;
  /** #ATTRIB-1 — first-party UTM / lead-source, persisted onto the user row. */
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_domain?: string;
  /** 2026-09-29 — persisted first / last touch (see services/attribution.ts captureTouch). */
  first_touch?: Record<string, string>;
  last_touch?: Record<string, string>;
}

export async function register(email: string, password: string, businessName: string, isFreeTrialSignup?: boolean, phone?: string, tosAccepted?: boolean, attribution?: SignupAttribution, honeypot?: { website?: string }): Promise<ApiResponse<any>> {
  return request('/auth/register', {
    method: 'POST',
    // honeypot: include `website` if the (hidden) form field was filled.
    // Real users never see the field; bots autofill anything that looks
    // like a contact/company URL. Backend silently 201s without creating
    // a row when this is non-empty.
    body: JSON.stringify({ email, password, businessName, isFreeTrialSignup, phone, tosAccepted, ...attribution, website: honeypot?.website }),
  });
}

export async function preRegister(email: string, password: string, businessName: string, phone?: string, tosAccepted?: boolean, attribution?: SignupAttribution, honeypot?: { website?: string }, outreachToken?: string | null): Promise<ApiResponse<AuthTokens & { userId: string }>> {
  return request('/auth/pre-register', {
    method: 'POST',
    // honeypot: the paid step's hidden `website` field. On this path the backend only LOGS a filled value (never
    // blocks) — see auth.routes.ts /pre-register.
    // outreachToken (#2491): the preview link a prospect came from, which is what lets them claim their demo account here.
    body: JSON.stringify({ email, password, businessName, phone, tosAccepted, ...attribution, website: honeypot?.website, outreachToken: outreachToken || undefined }),
  });
}

export async function confirmEmail(token: string, metaCookies?: { fbp?: string; fbc?: string }): Promise<ApiResponse<AuthTokens & { userId?: string; email?: string; signupSource?: string }>> {
  return request('/auth/confirm-email', {
    method: 'POST',
    body: JSON.stringify({ token, ...(metaCookies || {}) }),
  });
}

export async function resendConfirmation(email: string): Promise<ApiResponse<{ message: string }>> {
  return request('/auth/resend-confirmation', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function requestPasswordReset(email: string): Promise<ApiResponse<{ message: string }>> {
  return request('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function confirmPasswordReset(token: string, password: string): Promise<ApiResponse<{ message: string }>> {
  return request('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, password }),
  });
}

// D6 — non-consuming validity check so the reset page can show an expired/used
// link state up front instead of only failing on submit.
export async function validateResetToken(token: string): Promise<ApiResponse<{ valid: boolean }>> {
  return request('/auth/reset-password/validate', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// #18 — authenticated self-service password change (force-change-on-first-
// login for staff invites, but usable any time a signed-in user wants to set
// a new password). Returns fresh tokens — the new JWT already reflects
// mustChangePassword: false.
export async function changePassword(newPassword: string): Promise<ApiResponse<AuthTokens>> {
  return request('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ newPassword }),
  });
}

// #124 — client Account hub profile. email is read-only (login identity);
// businessName / firstName / lastName / phone are editable.
export interface AccountProfile {
  email: string;
  businessName: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export async function getProfile(): Promise<ApiResponse<AccountProfile>> {
  return request('/auth/profile');
}

export async function updateProfile(
  data: { businessName: string; firstName: string; lastName: string; phone: string },
): Promise<ApiResponse<AccountProfile>> {
  return request('/auth/profile', { method: 'PUT', body: JSON.stringify(data) });
}

export async function adminResetUserPassword(
  userId: string,
  password?: string,
): Promise<ApiResponse<{ password: string; userId: string; email: string }>> {
  return request(`/admin/users/${userId}/reset-password`, {
    method: 'POST',
    body: JSON.stringify(password ? { password } : {}),
  });
}

// #359 — emails the client the standard password-reset link (never returns the token/link itself).
export async function sendClientSetPasswordLink(userId: string): Promise<ApiResponse<{ emailed: boolean }>> {
  return request(`/client-mgmt/users/${userId}/send-set-password-link`, {
    method: 'POST',
  });
}

export async function adminImpersonateUser(
  userId: string,
  reason?: string,
): Promise<ApiResponse<AuthTokens & { user: JwtPayload }>> {
  // #336 — moved to the capability-gated client-mgmt router so `support` can reach it
  // (the old /admin/... path is requireAdminConsole-gated and excludes support).
  return request(`/client-mgmt/users/${userId}/impersonate`, {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
}

export async function getMe(): Promise<ApiResponse<JwtPayload>> {
  return request('/auth/me');
}

export async function logout(): Promise<void> {
  await request('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  });
  setTokens(null);
}

// ── Site API ──

export async function getSite(): Promise<ApiResponse<Site>> {
  return request('/site');
}

export async function getSiteById(siteId: string): Promise<ApiResponse<Site>> {
  return request(`/site/${siteId}`);
}

export async function getVersionHistory(siteId: string): Promise<ApiResponse<SiteVersion[]>> {
  return request(`/site/${siteId}/versions`);
}

export async function restoreVersion(siteId: string, versionId: string): Promise<ApiResponse<Site>> {
  return request(`/site/${siteId}/versions/${versionId}/restore`, { method: 'POST' });
}

export async function resetSite(siteId: string): Promise<ApiResponse<Site>> {
  return request(`/site/${siteId}/reset`, {
    method: 'POST',
    body: JSON.stringify({ confirm: true }),
  });
}

export async function updatePages(
  siteId: string,
  pages: { slug: string; title: string; html: string }[],
  changeDescription?: string,
): Promise<ApiResponse<Site>> {
  return request(`/site/${siteId}/pages`, {
    method: 'PATCH',
    body: JSON.stringify({ pages, changeDescription }),
  });
}

// ── Chat API ──

export interface ChatResult {
  pages: { slug: string; title: string; html: string }[];
  assistantMessage: string;
  tokensUsed: { input: number; output: number };
}

export interface ChatStreamOptions {
  elementContext?: SelectedElementContext;
  imagePlacement?: ImagePlacementContext;
}

export async function sendMessage(siteId: string, message: string, options?: ChatStreamOptions): Promise<ApiResponse<ChatResult>> {
  return request('/chat/message', {
    method: 'POST',
    body: JSON.stringify({ siteId, message, ...options }),
  });
}

export async function sendMessageStream(
  siteId: string,
  message: string,
  onToken: (text: string) => void,
  options?: ChatStreamOptions,
): Promise<ApiResponse<ChatResult>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}/chat/message`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ siteId, message, ...options }),
  });

  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      return sendMessageStream(siteId, message, onToken, options);
    }
    // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
    // gated — see handleSessionExpired); this request still fails either way.
    throw new Error('Session expired');
  }

  if (!res.body) {
    return { success: false, error: 'No response body from server' };
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ApiResponse<ChatResult> = { success: false, error: 'Stream ended unexpectedly' };
  let eventType = '';

  const processLines = (lines: string[]) => {
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (eventType === 'token') {
          try { onToken(JSON.parse(data)); } catch { /* skip */ }
        } else if (eventType === 'done') {
          try {
            result = { success: true, data: JSON.parse(data) };
          } catch {
            result = { success: false, error: 'Failed to parse final response' };
          }
        } else if (eventType === 'error') {
          try {
            const errData = JSON.parse(data);
            result = { success: false, error: errData.error || 'Unknown error' };
          } catch {
            result = { success: false, error: data };
          }
        }
        eventType = '';
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    processLines(lines);
  }
  if (buffer.trim()) {
    processLines(buffer.split('\n'));
  }

  return result;
}

export async function getChatHistory(siteId: string): Promise<ApiResponse<ChatMessage[]>> {
  return request(`/chat/history/${siteId}`);
}

// ── Admin API ──

export async function getAdminSettings(): Promise<ApiResponse<AdminSettings>> {
  return request('/admin/settings');
}

export async function updateAdminSettings(settings: Partial<AdminSettings>): Promise<ApiResponse<{ id: string; updatedAt: string }>> {
  return request('/admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// #928 — Admin help-knowledge editor (kb_articles). The help bot answers from
// these; editing them here updates the live KB without a deploy.
export interface KbArticle {
  slug: string;
  title: string;
  keywords: string[];
  content: string;
  // Phase 2.4 — AI-gating. 'internal' articles are staff-only and never reach
  // the help bot; aiVisible=false hides an article from the bot even if public.
  audience?: 'public' | 'internal' | 'both';
  aiVisible?: boolean;
  category?: string | null;
  updatedAt?: string;
}

export async function getKbArticles(): Promise<ApiResponse<KbArticle[]>> {
  return request('/admin/kb/articles');
}

// Phase 2.4 — full-text KB search (staff; includes internal docs). Backs the
// sales-desk lookup + is available to any staff surface.
export async function searchKb(q: string, limit?: number): Promise<ApiResponse<KbArticle[]>> {
  const params = new URLSearchParams({ q });
  if (limit != null) params.set('limit', String(limit));
  return request(`/admin/kb/search?${params.toString()}`);
}

export async function saveKbArticle(article: KbArticle): Promise<ApiResponse<{ slug: string }>> {
  return request(`/admin/kb/articles/${encodeURIComponent(article.slug)}`, {
    method: 'PUT',
    body: JSON.stringify({
      title: article.title,
      keywords: article.keywords,
      content: article.content,
      audience: article.audience,
      aiVisible: article.aiVisible,
      category: article.category,
    }),
  });
}

export async function deleteKbArticle(slug: string): Promise<ApiResponse<{ slug: string }>> {
  return request(`/admin/kb/articles/${encodeURIComponent(slug)}`, { method: 'DELETE' });
}

export interface IntegrationTestResult {
  ok: boolean;
  message: string;
  model?: string;
  usage?: { input: number; output: number };
}

export async function testIntegration(integration: 'claude' | 's3' | 'twentyi' | 'sendgrid' | 'authorizenet' | 'openai' | 'pexels' | 'outscraper' | 'unsplash' | 'google-maps' | 'slack'): Promise<ApiResponse<IntegrationTestResult>> {
  return request(`/admin/test/${integration}`, { method: 'POST' });
}

// slack-3ch — test a SPECIFIC pasted webhook URL (Tickets / Errors channel) without saving first.
// Reuses /admin/test/slack, which tests req.body.webhookUrl when one is provided.
export async function testSlackWebhook(webhookUrl: string): Promise<ApiResponse<IntegrationTestResult>> {
  return request('/admin/test/slack', { method: 'POST', body: JSON.stringify({ webhookUrl }) });
}

export async function getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
  return request('/admin/dashboard');
}

// ── Pending Package Deletions (20i) ──
// #267 — billing's deleteSite() now defers the final irreversible 20i package
// delete to a human instead of running it unattended; this is the admin queue
// that reviews + confirms (or dismisses) each one.
export interface PendingPackageDeletion {
  id: string;
  packageId: string;
  kind: 'production' | 'staging';
  domain: string | null;
  projectId: string | null;
  clientId: string | null;
  source: string;
  note: string | null;
  createdAt: string;
}

export async function getPendingPackageDeletions(): Promise<ApiResponse<PendingPackageDeletion[]>> {
  return request('/admin/pending-package-deletions');
}

export async function confirmPendingPackageDeletion(id: string): Promise<ApiResponse<{ id: string; resolvedAt: string }>> {
  return request(`/admin/pending-package-deletions/${id}/confirm`, { method: 'POST' });
}

export async function dismissPendingPackageDeletion(id: string): Promise<ApiResponse<{ id: string; resolvedAt: string }>> {
  return request(`/admin/pending-package-deletions/${id}/dismiss`, { method: 'POST' });
}

// ── Admin actions taken from the customer view (Login as Customer) ──
// While impersonating, /api/admin/* calls must carry the admin's PARKED token
// (parkedAdminToken.ts), never the active customer session.
export { getImpersonatorRole } from './parkedAdminToken';

export type MaintenanceRepublishResponse = ApiResponse<Partial<DeploymentData> & { unpublishedPages?: number }>;

/**
 * Admin "Republish (no customer email)": POST /admin/projects/:id/maintenance-republish
 * reruns the normal deploy for a live site without the customer's "Your website
 * is live!" email. While impersonating it sends the parked admin token;
 * otherwise the admin's own session via request(). Refusals are 409s with a
 * `code`: NOT_LIVE, DOMAIN_UNVERIFIED, HOMEPAGE_NOT_BUILT, or UNPUBLISHED_EDITS
 * (data.unpublishedPages), which the admin can confirm by resending with
 * includeUnpublishedEdits.
 */
export async function adminMaintenanceRepublish(
  projectId: string,
  opts: { includeUnpublishedEdits?: boolean } = {},
): Promise<MaintenanceRepublishResponse> {
  const path = `/admin/projects/${projectId}/maintenance-republish`;
  const init: RequestInit = { method: 'POST', body: JSON.stringify({ includeUnpublishedEdits: opts.includeUnpublishedEdits === true }) };
  if (!hasParkedAdminToken()) return request(path, init);
  return parkedAdminRequest(API_BASE, path, init, { forbiddenError: 'Only full admins can run a maintenance republish.' });
}

// ── API Keys (Agency tier) ──

export interface ApiKeyListItem {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

export interface ApiKeyCreateResult {
  id: string;
  name: string;
  key: string; // plaintext, shown ONCE
  prefix: string;
  scopes: string[];
  createdAt: string;
  warning: string;
}

export async function listApiKeys(): Promise<ApiResponse<{ keys: ApiKeyListItem[]; canCreate: boolean; lockReason: string | null }>> {
  return request('/api-keys');
}

export async function createApiKey(name: string): Promise<ApiResponse<ApiKeyCreateResult>> {
  return request('/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function revokeApiKey(id: string): Promise<ApiResponse<{ id: string; revoked: boolean }>> {
  return request(`/api-keys/${id}`, { method: 'DELETE' });
}

// ── Admin API Keys (for support ticket automation) ──

export interface AdminApiKeyListItem {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  createdByEmail?: string | null;
  lastUsedAt?: string | null;
  revokedAt?: string | null;
  createdAt: string;
}

export async function listAdminApiKeys(): Promise<ApiResponse<{ keys: AdminApiKeyListItem[] }>> {
  return request('/admin/api-keys');
}

export async function createAdminApiKey(name: string): Promise<ApiResponse<ApiKeyCreateResult>> {
  return request('/admin/api-keys', {
    method: 'POST',
    body: JSON.stringify({ name }),
  });
}

export async function revokeAdminApiKey(id: string): Promise<ApiResponse<{ id: string; revoked: boolean }>> {
  return request(`/admin/api-keys/${id}`, { method: 'DELETE' });
}

// ── Email Templates ──

export interface EmailTemplateVariableDef {
  name: string;
  description: string;
}

export interface EmailTemplateSummary {
  key: string;
  label: string;
  category: string;
  recipient: 'customer' | 'admin';
  description: string;
  variables: EmailTemplateVariableDef[];
  defaultSubject: string;
  currentSubject: string;
  currentHtml: string;
  isCustomized: boolean;
}

export async function listEmailTemplates(): Promise<ApiResponse<{ templates: EmailTemplateSummary[] }>> {
  return request('/admin/email-templates');
}

export async function updateEmailTemplate(
  key: string,
  body: { subject: string; html: string },
): Promise<ApiResponse<EmailTemplateSummary>> {
  return request(`/admin/email-templates/${encodeURIComponent(key)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function resetEmailTemplate(key: string): Promise<ApiResponse<{ key: string; reset: true }>> {
  return request(`/admin/email-templates/${encodeURIComponent(key)}`, { method: 'DELETE' });
}

export async function previewEmailTemplate(
  key: string,
  body: { subject: string; html: string; sampleVariables?: Record<string, string | number> },
): Promise<ApiResponse<{ subject: string; html: string }>> {
  return request(`/admin/email-templates/${encodeURIComponent(key)}/preview`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function sendTestEmailTemplate(
  key: string,
  body: { to: string; subject?: string; html?: string },
): Promise<ApiResponse<{ sent: true }>> {
  return request(`/admin/email-templates/${encodeURIComponent(key)}/send-test`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function exportProjectHtml(projectId: string): Promise<Response> {
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}/projects/${projectId}/export`, {
    method: 'GET',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  return res;
}

export async function syncAgeGate(projectId: string): Promise<ApiResponse<{ enabled: boolean; minAge?: number; action: 'installed' | 'removed'; message?: string }>> {
  return request(`/projects/${projectId}/age-gate/sync`, { method: 'POST' });
}

// §28 — record per-project acceptance of the AI-legal-content disclaimer.
export async function acceptLegalDisclaimer(projectId: string): Promise<ApiResponse<{ acceptedAt: string }>> {
  return request(`/projects/${projectId}/legal-consent`, { method: 'POST' });
}

export async function syncPortfolioSettings(projectId: string): Promise<ApiResponse<{ settings: PortfolioSettings; action: 'queued'; message?: string }>> {
  return request(`/projects/${projectId}/portfolio/sync`, { method: 'POST' });
}

// #2230 (W2-N) — the sync route above answers "Saved!" and runs the live-site update in the
// background; these let the dashboard learn whether it actually landed and retry if not.
export type SyncOutcomeStatus = 'idle' | 'syncing' | 'ok' | 'failed';

export interface SyncOutcome {
  status: SyncOutcomeStatus;
  lastError: string | null;
  lastSyncedAt: string | null;
}

export async function getPortfolioSyncStatus(projectId: string): Promise<ApiResponse<SyncOutcome>> {
  return request(`/projects/${projectId}/portfolio/sync-status`);
}

export async function retryPortfolioSync(projectId: string): Promise<ApiResponse<{ settings: PortfolioSettings; action: 'queued'; message?: string }>> {
  return request(`/projects/${projectId}/portfolio/sync/retry`, { method: 'POST' });
}

// ── Support Tickets ──

import type { Ticket, TicketMessage, TicketCategory, TicketStatus, TicketPriority } from '@siteforge/shared';

export async function createTicket(opts: {
  subject: string;
  body: string;
  category: TicketCategory;
  projectId?: string;
  // #123 — admin/support may open a ticket ON BEHALF of a client (the backend
  // accepts clientId only from an admin-actor with tickets.view).
  clientId?: string;
  attachments?: File[];
  // Phase 3.2 — attach a chat transcript as a second, source-tagged message.
  // conciergeConversationId lets the backend load the transcript authoritatively
  // from concierge_messages; chatTranscript is a client-supplied transcript for
  // the help-drawer / editor chats (which aren't concierge-persisted).
  conciergeConversationId?: string;
  chatTranscript?: string;
  chatSource?: 'concierge' | 'help_drawer' | 'editor';
}): Promise<ApiResponse<{ ticket: Ticket; firstMessage: TicketMessage }>> {
  const fd = new FormData();
  fd.append('subject', opts.subject);
  fd.append('body', opts.body);
  fd.append('category', opts.category);
  if (opts.projectId) fd.append('projectId', opts.projectId);
  if (opts.clientId) fd.append('clientId', opts.clientId);
  if (opts.conciergeConversationId) fd.append('conciergeConversationId', opts.conciergeConversationId);
  if (opts.chatTranscript) fd.append('chatTranscript', opts.chatTranscript);
  if (opts.chatSource) fd.append('chatSource', opts.chatSource);
  (opts.attachments || []).forEach((f) => fd.append('attachments', f));
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}/tickets`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  // Bug 14 (Apr 22): a 413/Nginx-error response is HTML, not JSON. Tolerate
  // that so the user sees a friendly message instead of a raw JSON parse error.
  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('application/json')) {
    if (res.status === 413) {
      return { success: false, error: 'File is too large. Please keep each attachment under 10 MB.' } as any;
    }
    return { success: false, error: `Upload failed (${res.status}). Please try again or attach smaller files.` } as any;
  }
  try { return await res.json(); } catch {
    return { success: false, error: 'Upload failed. Please try again or attach smaller files.' } as any;
  }
}

export async function listTickets(status: 'open' | 'awaiting_customer' | 'resolved' | 'closed' | 'all' = 'all', page = 1, pageSize = 20): Promise<ApiResponse<{ tickets: Ticket[]; total: number; page: number; pageSize: number }>> {
  return request(`/tickets?status=${encodeURIComponent(status)}&page=${page}&pageSize=${pageSize}`);
}

export async function getTicket(id: string): Promise<ApiResponse<{ ticket: Ticket; messages: TicketMessage[] }>> {
  return request(`/tickets/${id}`);
}

export async function replyToTicket(
  id: string,
  body: string,
  opts: { attachments?: File[]; isInternal?: boolean } | File[] = {},
): Promise<ApiResponse<{ message: TicketMessage; status: TicketStatus }>> {
  // Backwards-compat: second argument used to be File[]. Accept either shape.
  const normalized: { attachments?: File[]; isInternal?: boolean } = Array.isArray(opts)
    ? { attachments: opts }
    : opts;
  const fd = new FormData();
  fd.append('body', body);
  if (normalized.isInternal) fd.append('isInternal', 'true');
  (normalized.attachments || []).forEach((f) => fd.append('attachments', f));
  const token = getAccessToken();
  const res = await fetch(`${API_BASE}/tickets/${id}/messages`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  return res.json();
}

export async function closeTicket(id: string): Promise<ApiResponse<{ id: string; status: TicketStatus }>> {
  return request(`/tickets/${id}/close`, { method: 'POST' });
}

// Admin
export async function listAdminTickets(opts: { status?: string; category?: string; assignee?: string; page?: number; pageSize?: number } = {}): Promise<ApiResponse<{ tickets: any[]; total: number; page: number; pageSize: number }>> {
  const params = new URLSearchParams();
  if (opts.status) params.set('status', opts.status);
  if (opts.category) params.set('category', opts.category);
  if (opts.assignee) params.set('assignee', opts.assignee);
  if (opts.page) params.set('page', String(opts.page));
  if (opts.pageSize) params.set('pageSize', String(opts.pageSize));
  const qs = params.toString();
  return request(`/tickets/admin/all${qs ? `?${qs}` : ''}`);
}

export async function updateTicket(
  id: string,
  patch: { status?: TicketStatus; priority?: TicketPriority; category?: TicketCategory; assignedAdminId?: string | null },
): Promise<ApiResponse<Ticket>> {
  return request(`/tickets/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export async function getAdminClients(
  page = 1,
  pageSize = 20,
  search = '',
  partnerId = '',
  // #2178(5) — 'exclude' (default) | 'only' | 'include'.
  abandoned = 'exclude'
): Promise<ApiResponse<unknown> & { clients?: unknown[]; total?: number }> {
  const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search.trim()) qs.set('search', search.trim());
  if (partnerId.trim()) qs.set('partnerId', partnerId.trim());
  if (abandoned && abandoned !== 'exclude') qs.set('abandoned', abandoned);
  return request(`/admin/clients?${qs.toString()}`);
}

// #client-detail: read-only billing + account summary for one client (admin).
export interface ClientDetailProfile {
  id: string;
  userId: string;
  email: string;
  businessName: string | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  domain: string | null;
  location: string | null;
  tier: string | null;
  /** #238 — the plan name to actually DISPLAY (real paid package name, or
   * 'Free' only when there's no qualifying paid order). Prefer this over
   * `tier`, which is the raw catalog column and collapses every paid tier
   * (Grow/Scale/Starter) to 'starter'. */
  planName: string;
  partnerId: string | null;
  partnerCompanyName: string | null;
  isActive: boolean;
  emailConfirmed: boolean;
  lastLoginAt: string | null;
  signupSource: string | null;
  outreachPending: boolean;
  convertedAt: string | null;
  createdAt: string;
}
export interface ClientDetailOrder {
  id: string;
  packageName: string | null;
  packageSlug: string | null;
  status: string;
  initialAmountCents: number | null;
  monthlyAmountCents: number | null;
  discountAmountCents: number;
  paidAt: string | null;
  billingPeriodEnd: string | null;
  nextBillingAt: string | null;
  cancelledAt: string | null;
  createdAt: string;
  addons: Array<{ name: string; slug: string; priceCents: number }>;
}
export interface ClientDetailPayment {
  type: string;
  amountCents: number;
  status: string;
  gateway: string | null;
  gatewayTransactionId: string | null;
  createdAt: string;
}
export interface ClientDetailTicket {
  id: string; subject: string; category: string; status: string; priority: string;
  createdAt: string; updatedAt: string;
}
export interface ClientDetailActivity {
  id: string; type: string; body: string;
  authorEmail: string | null; ticketId: string | null; createdAt: string;
}
export interface ClientDetail {
  profile: ClientDetailProfile;
  currentOrder: ClientDetailOrder | null;
  orders: ClientDetailOrder[];
  payments: ClientDetailPayment[];
  collectedCents: number;
  aiSpend: { total: number; byProvider: Array<{ provider: string; total: number }> };
  helpBalance: { totalHours: number; usedHours: number; remainingHours: number; pendingRequests: number };
  // #122/#123 — History section.
  supportTickets?: ClientDetailTicket[];
  activity?: ClientDetailActivity[];
}
// #122/#123/#125 — the client record is served from the capability-gated
// client-management router (reachable by admin/sub_admin/support via caps),
// NOT the admin-only /admin/clients route, so the support role can open it.
export async function getClientDetail(id: string): Promise<ApiResponse<ClientDetail>> {
  return request(`/client-mgmt/clients/${encodeURIComponent(id)}`);
}
export async function logClientActivity(
  id: string,
  data: { type: 'call' | 'note'; body: string; ticketId?: string },
): Promise<ApiResponse<ClientDetailActivity>> {
  return request(`/client-mgmt/clients/${encodeURIComponent(id)}/activity`, { method: 'POST', body: JSON.stringify(data) });
}
export async function changeClientPlan(
  id: string,
  data: { packageSlug?: string; addonSlugs?: string[]; idempotencyKey?: string },
): Promise<ApiResponse<{ orderId: string; packageName: string; monthlyAmountCents: number; appliesNextCycle: boolean; hasCard: boolean; setupCharges: Array<{ addon: string; amountCents: number; charged: boolean; note?: string }>; deferredSetupFeesCents: number }>> {
  return request(`/client-mgmt/clients/${encodeURIComponent(id)}/change-plan`, { method: 'POST', body: JSON.stringify(data) });
}

export async function createClient(data: {
  email: string;
  password: string;
  businessName: string;
  packageId?: string;
  domain?: string;
}): Promise<ApiResponse<{ userId: string; clientId: string }>> {
  return request('/admin/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteAdminClient(clientId: string): Promise<ApiResponse<{ deletedClientId: string; deletedEmail: string; deletedBusinessName: string | null }>> {
  return request(`/admin/clients/${clientId}`, { method: 'DELETE' });
}

export async function deleteAdminClientsBatch(ids: string[]): Promise<ApiResponse<{
  deletedCount: number;
  deleted: Array<{ clientId: string; email: string; businessName: string | null }>;
  // #2264 — the internal system account is skipped (never deleted), not silently dropped;
  // the server reports which ids were skipped and why so the UI can tell the admin.
  skippedCount?: number;
  skipped?: Array<{ clientId: string; email: string; businessName: string | null }>;
}>> {
  return request('/admin/clients/delete-batch', {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}

// ── Billing API (Public) ──

export async function getPackages(brandSlug?: string | null): Promise<ApiResponse<{
  packages: unknown[];
  addons: unknown[];
  setupSlaPolicyText?: string;
  setupSlaCalendarUrl?: string;
  // Admin kill-switch for free signup (Zach, 2026-10-14) — default-on.
  freeSignupEnabled?: boolean;
}>> {
  // #2166 — a reseller's branded checkout passes its brand slug so the prices shown are the SAME ones /checkout
  // charges (partnerRetailForBrand / partnerRetailForAddon). Omitted (every other caller): the platform price.
  return request(brandSlug ? `/billing/packages?brandSlug=${encodeURIComponent(brandSlug)}` : '/billing/packages');
}

// #2247 — holdToken: the visitor's own checkout hold on this name, so re-searching it never reads "being purchased".
export async function searchDomains(domain: string, holdToken?: string): Promise<ApiResponse<unknown>> {
  return request('/billing/domain-search', {
    method: 'POST',
    body: JSON.stringify(holdToken ? { domain, holdToken } : { domain }),
  });
}

// Phase 2B — project collaborators (invite + accept).
export interface ProjectCollaborator {
  id: string;
  email: string;
  role: 'editor' | 'viewer';
  accepted: boolean;
  acceptedAt: string | null;
  createdAt: string;
}
export async function listCollaborators(projectId: string): Promise<ApiResponse<ProjectCollaborator[]>> {
  return request(`/projects/${projectId}/collaborators`);
}
export async function inviteCollaborator(projectId: string, email: string, role: 'editor' | 'viewer' = 'editor'): Promise<ApiResponse<{ id: string }>> {
  return request(`/projects/${projectId}/collaborators`, {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  });
}
export async function removeCollaborator(projectId: string, collabId: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/collaborators/${collabId}`, { method: 'DELETE' });
}
export async function acceptCollabInvite(token: string): Promise<ApiResponse<{ projectId: string }>> {
  return request(`/projects/collab/accept`, {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

// Phase 2A — post-publish onboarding checklist state per project.
export interface OnboardingTask {
  key: 'phone' | 'services' | 'reviews' | 'domain' | 'google';
  completed: boolean;
  completedAt: string | null;
}
export async function getProjectOnboarding(projectId: string): Promise<ApiResponse<OnboardingTask[]>> {
  return request(`/projects/${projectId}/onboarding`);
}

// Phase 1C / 1D — admin-managed upsell addons keyed by placement
// ('published_page' | 'settings_aside' | 'dashboard_promo'). Admins
// configure these in /admin/packages (Add-ons section); surfaces that
// display upsells call listAddonsByPlacement and render whatever the
// admin has published.
export interface PlacementAddon {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  priceCents: number;
  features: string[];
  placement: string | null;
}
export async function listAddonsByPlacement(placement: string): Promise<ApiResponse<PlacementAddon[]>> {
  return request(`/billing/addons?placement=${encodeURIComponent(placement)}`);
}

// Phase 1B-2 — per-project chat transcript
import type { ProjectChatMessage } from '@siteforge/shared';

export async function getProjectChat(projectId: string, slug: string, limit = 200, variantId?: string): Promise<ApiResponse<ProjectChatMessage[]>> {
  // #1241 (VITE_SF_VARIATION_SCOPE) — per-variation chat threads in the guided
  // flow. When a variantId is supplied the backend scopes the transcript to that
  // design variation; omitted (or flag off) = today's slug-only transcript.
  let url = `/projects/${projectId}/chat?slug=${encodeURIComponent(slug)}&limit=${limit}`;
  if (variantId && import.meta.env.VITE_SF_VARIATION_SCOPE !== 'off') {
    url += `&variantId=${encodeURIComponent(variantId)}`;
  }
  return request(url);
}

export async function clearProjectChat(projectId: string, slug: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/chat?slug=${encodeURIComponent(slug)}`, { method: 'DELETE' });
}

/**
 * Run a tool-generated edit prompt through Claude Haiku and return a
 * one-sentence friendly version for the chat panel. Backend rejects empty
 * prompts and falls back to `null` if Haiku fails — caller must handle a
 * null `humanized` by showing the raw prompt.
 */
export async function humanizeChatPrompt(
  projectId: string,
  prompt: string,
): Promise<ApiResponse<{ humanized: string | null }>> {
  return request(`/projects/${projectId}/chat/humanize`, {
    method: 'POST',
    body: JSON.stringify({ prompt }),
  });
}

/**
 * Heuristic: does this prompt look like one assembled by an editor tool
 * (image placement, targeted edit, etc.) rather than typed by the user?
 * Tool prompts wrap the user's intent in technical scaffolding that's
 * noisy in the chat panel — we humanize those via Haiku before display.
 */
export function isToolGeneratedPrompt(prompt: string): boolean {
  const head = prompt.slice(0, 60);
  return /^(IMAGE PLACEMENT EDIT|IMAGE INSERT EDIT|TARGETED EDIT|Place the uploaded image|Place the image)/i.test(head);
}

/**
 * Deterministic, offline fallback for turning a tool-generated edit prompt into
 * a clean chat-panel label when the Haiku humanizer is unavailable. Guarantees
 * the raw technical scaffolding (e.g. "TARGETED EDIT — ...", "IMAGE PLACEMENT
 * EDIT — ...") never lands in the transcript — it surfaces what the user
 * actually expressed (their instruction) instead of our wrapper.
 */
export function stripToolScaffolding(prompt: string): string {
  const p = prompt.trim();
  // Targeted element edit — the real instruction is after "User instruction:".
  const ui = p.match(/User instruction:\s*([\s\S]+)$/i);
  if (/^TARGETED EDIT/i.test(p) && ui) return ui[1].trim();
  if (/^IMAGE PLACEMENT EDIT/i.test(p)) return 'Replaced an image on the page';
  if (/^IMAGE INSERT EDIT/i.test(p)) return 'Added an image to the page';
  if (/^Place the uploaded image/i.test(p)) {
    const after = p.replace(/^Place the uploaded image[^.]*\.\s*/i, '').trim();
    return after || 'Placed an image on the page';
  }
  if (/^Place the image/i.test(p)) return 'Placed an image on the page';
  // Drop any leading viewport-context bracket, e.g. "[User is viewing in MOBILE …]".
  return p.replace(/^\[[^\]]*\]\s*/, '').trim();
}

/**
 * Append a system/user/assistant row directly (for non-AI events like
 * section reorder, undo, or manual notices). Regular AI edits go through
 * the SSE /generate endpoint and write their own user + assistant rows.
 */
export async function appendProjectChat(projectId: string, payload: {
  slug: string;
  role: 'system' | 'user' | 'assistant';
  content: string;
  changeSummary?: string;
  // 'help' renders the content as a visible assistant bubble (Feature 5) — used
  // by the #911-fix no-op move hand-off to show its positioning question.
  outcome?: 'applied' | 'partial' | 'failed' | 'cancelled' | 'skipped' | 'help' | 'no_change_needed';
  attachmentKind?: string;
  attachmentMeta?: Record<string, unknown>;
}): Promise<ApiResponse<{ id: string }>> {
  return request(`/projects/${projectId}/chat`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// Phase 1E — BYO domain validation for the 3-option picker.
export interface CheckDomainResult {
  domain: string;
  exists: boolean;       // does it resolve in public DNS right now
  pointsAtUs: boolean;   // A / NS already point at our servers
  inUse: boolean;        // already attached to a SiteForge project
  // #850: 'platform-other-account' = the domain resolves on our hosting
  // platform but is attached to ANOTHER account (usually the customer's
  // previous provider) — usable, but go-live stays gated until it points at us.
  reason: 'invalid-format' | 'not-found' | 'points-at-us' | 'needs-dns-setup' | 'in-use-by-own-project' | 'in-use-by-other' | 'platform-other-account';
  message: string;
  nameservers?: string[];
  aRecords?: string[];
  verificationReason?: string;
}
export async function checkDomain(domain: string, holdToken?: string): Promise<ApiResponse<CheckDomainResult>> {
  return request('/projects/check-domain', {
    method: 'POST',
    body: JSON.stringify(holdToken ? { domain, holdToken } : { domain }),
  });
}

export async function getDomainPricing(): Promise<ApiResponse<unknown>> {
  return request('/billing/domain-pricing');
}

export interface DomainSuggestion {
  domain: string;
  available: boolean;
  price?: number;
}

export async function suggestDomains(data: {
  domain: string;
  industry?: string;
  location?: string;
  minWords?: number;
  maxWords?: number;
}): Promise<ApiResponse<{ suggestions: DomainSuggestion[] }>> {
  return request('/billing/domain-suggest', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function checkout(data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// #2247 (m27 W3) — reserve the domain the visitor just picked in checkout (up to 20 minutes). holdToken: this
// browser's existing hold on the SAME name (returned unchanged); previousHoldToken: its hold on a name it just
// switched away from (released). holdsEnabled=false when the server's SF_DOMAIN_HOLDS kill switch is off.
export interface DomainHoldResult {
  holdsEnabled: boolean;
  domain?: string;
  holdToken?: string;
  state?: 'pending' | 'paid';
  expiresAt?: string | null;
}
export async function createDomainHold(domain: string, holdToken?: string, previousHoldToken?: string): Promise<ApiResponse<DomainHoldResult>> {
  return request('/billing/domain-holds', {
    method: 'POST',
    body: JSON.stringify({ domain, ...(holdToken ? { holdToken } : {}), ...(previousHoldToken ? { previousHoldToken } : {}) }),
  });
}

export interface AdminDomainHold {
  id: string;
  domain: string;
  state: 'pending' | 'paid' | 'released' | 'claimed';
  clientId: string | null;
  clientEmail: string | null;
  orderId: string | null;
  expiresAt: string | null;
  createdAt: string;
  paidAt: string | null;
  releasedAt: string | null;
  releaseReason: string | null;
}
export async function getAdminDomainHolds(scope: 'active' | 'all' = 'active'): Promise<ApiResponse<AdminDomainHold[]>> {
  return request(`/admin/domain-holds${scope === 'all' ? '?scope=all' : ''}`);
}
export async function releaseAdminDomainHold(id: string): Promise<ApiResponse<{ released: boolean; domain: string; previousState: string }>> {
  return request(`/admin/domain-holds/${encodeURIComponent(id)}/release`, { method: 'POST' });
}

// Phase 0.1 (Accept.js) — public config for browser-side card tokenization
// (API Login ID + public Client Key + sandbox flag; never the Transaction Key).
export async function getAcceptJsConfig(): Promise<ApiResponse<unknown>> {
  return request('/billing/accept-js-config');
}

export async function getOrders(): Promise<ApiResponse<unknown>> {
  return request('/billing/orders');
}

export async function getOrder(id: string): Promise<ApiResponse<unknown>> {
  return request(`/billing/orders/${id}`);
}

export async function cancelOrder(id: string): Promise<ApiResponse<unknown>> {
  return request(`/billing/orders/${id}/cancel`, { method: 'POST' });
}

/**
 * #2167 — a one-time setup add-on (Done-for-you) needs the customer's explicit confirmation of the exact fee
 * before it's charged: `confirmSetupCents` must equal the `setupDueCents` the modal showed (GET .../plan-prices),
 * or the server refuses with 409 SETUP_CONFIRMATION_REQUIRED instead of charging. `requestId` (8-64 chars,
 * [A-Za-z0-9-]) scopes the server's idempotency key — reuse the same id for a double-submit of the same
 * attempt; mint a fresh one after a definite decline so a genuine retry isn't deduped against the old failure
 * (never after SETUP_PAYMENT_UNCONFIRMED, a timeout where the charge may have gone through). Both are optional:
 * a plain plan/monthly-addon change (no new one-time add-on) needs neither.
 *
 * The response can carry `code` (SETUP_CONFIRMATION_REQUIRED / NO_CARD_ON_FILE / SETUP_PAYMENT_FAILED /
 * SETUP_PAYMENT_UNCONFIRMED / SETUP_PAYMENT_IN_PROGRESS — see billing.routes.ts POST /orders/:id/upgrade), `setupDueCents` (409: the real
 * fee to show back to the customer) and, on success, `setupChargedCents` (the one-time fee that was just
 * charged). These ride alongside the normal ApiResponse envelope rather than inside `data`, so they're typed
 * here as an intersection instead of widening the shared ApiResponse for every other caller.
 */
export async function upgradeOrder(
  id: string,
  data: { packageSlug?: string; addonSlugs?: string[]; confirmSetupCents?: number; requestId?: string },
): Promise<ApiResponse<unknown> & { setupDueCents?: number; setupChargedCents?: number }> {
  return request<unknown>(`/billing/orders/${id}/upgrade`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * #2161: the monthly price a plan change on this order would charge, per plan and add-on (what /upgrade stores).
 * #2167: each add-on also carries its one-time setup fee + whether it's a one-time add-on (no monthly, a setup
 * fee > 0 — e.g. Done-for-you) so the Upgrade modal can show "Due today" for a NEW one-time purchase, exactly
 * what POST .../upgrade will charge.
 */
export async function getOrderPlanPrices(id: string): Promise<ApiResponse<{
  packages: Array<{ slug: string; monthlyCents: number }>;
  addons: Array<{ slug: string; monthlyCents: number; setupCents: number; oneTime: boolean }>;
}>> {
  return request(`/billing/orders/${id}/plan-prices`);
}

export async function resumeOrder(id: string): Promise<ApiResponse<unknown>> {
  return request(`/billing/orders/${id}/resume`, { method: 'POST' });
}

export async function reactivateOrder(id: string): Promise<ApiResponse<unknown>> {
  return request(`/billing/orders/${id}/reactivate`, { method: 'POST' });
}

// Phase 1.3 — customer self-service "Update card" page (BillingPage.tsx) and
// the admin equivalent (support-call card updates from AdminClients). Both
// accept EITHER an Accept.js opaque nonce (cardDataDescriptor/cardDataValue)
// OR a raw card (cardNumber/cardExpiry/cardCvc) — see services/accept-js.ts.
export async function updatePaymentMethod(data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request('/billing/payment-method', { method: 'PUT', body: JSON.stringify(data) });
}

export async function adminUpdateClientPaymentMethod(clientId: string, data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request(`/admin/clients/${clientId}/payment-method`, { method: 'PUT', body: JSON.stringify(data) });
}

// Phase 1.5c — admin one-time charge against a client's CIM card on file
// (e.g. a support-call upsell, or attaching + billing a one-off add-on).
// Provide EITHER amountCents+description OR addonSlug. idempotencyKey is
// REQUIRED: mint one UUID per charge-modal instance (see AdminChargeModal)
// and reuse it across retries from that instance — the backend
// (billing.service chargeAndRecord) dedups on it, so a double-click can
// never double-charge. NOTE: a FAILED attempt permanently burns its key
// (the backend replays the cached decline rather than re-attempting), so a
// genuine retry needs a fresh key — i.e. a new modal instance.
export async function adminChargeClient(
  clientId: string,
  data: { amountCents?: number; addonSlug?: string; description?: string; idempotencyKey: string },
): Promise<ApiResponse<{ transactionId?: string; alreadyProcessed?: boolean }>> {
  return request(`/admin/clients/${clientId}/charge`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Payment Links (Phase 2.1) — tokenized /pay/:token links. GET/pay/card-update
// are public (no auth token required); createPaymentLink is staff-only and
// relies on request()'s normal Authorization-if-present behavior — the backend
// 401/403s if the logged-in user's role doesn't qualify. ──
export async function getPaymentLink(token: string): Promise<ApiResponse<{
  linkType: 'checkout' | 'card_update';
  packageName: string | null;
  amountCents: number | null;
  monthlyCents: number | null;
  domain: string | null;
  addons: Array<{ id: string; name: string; priceCents: number }>;
  expiresAt: string;
}>> {
  return request(`/payment-links/${token}`);
}

export async function payPaymentLink(
  token: string,
  data: Record<string, unknown>,
): Promise<ApiResponse<{ orderId: string; isNewAccount: boolean }>> {
  return request(`/payment-links/${token}/pay`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePaymentLinkCard(
  token: string,
  data: Record<string, unknown>,
): Promise<ApiResponse<unknown>> {
  return request(`/payment-links/${token}/card-update`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createPaymentLink(
  data: Record<string, unknown>,
): Promise<ApiResponse<{ token: string; url: string }>> {
  return request('/payment-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ── Invoices (Phase 1.4b) — customer invoice history + admin order-invoices ──
// Mirrors packages/backend/src/services/invoice.service.ts's InvoiceRow shape.

export interface InvoiceLineItem {
  description: string;
  amountCents: number;
}

export interface Invoice {
  id: string;
  order_id: string | null;
  payment_transaction_id: string | null;
  invoice_number: string;
  amount_cents: number;
  line_items: InvoiceLineItem[] | null;
  issued_at: string;
  pdf_path: string | null;
  created_at: string;
}

export async function getInvoices(): Promise<ApiResponse<Invoice[]>> {
  return request('/billing/invoices');
}

export async function getAdminOrderInvoices(orderId: string): Promise<ApiResponse<Invoice[]>> {
  return request(`/admin/orders/${orderId}/invoices`);
}

// GET /billing/invoices/:id/pdf is authenticated + ownership-checked, so it
// needs a Bearer header — a plain <a href>/window.open() to this URL sends
// no auth and 401s. Callers that just need the raw path (e.g. to compose
// their own request) can use this; everyone else should call
// downloadInvoicePdf() below, which actually attaches the token.
export function invoicePdfUrl(invoiceId: string): string {
  return `${API_BASE}/billing/invoices/${invoiceId}/pdf`;
}

/**
 * Download an invoice PDF with auth. Same problem/solution as
 * AdvancedTab.tsx's project export and PartnerSettings.tsx's tax-form
 * download: the PDF route requires a Bearer token, which only a JS fetch
 * can attach (a bare <a href> or window.open can't set headers). We fetch
 * the bytes ourselves, then hand the browser a blob: URL via a synthetic
 * <a download> to trigger the save.
 */
export async function downloadInvoicePdf(invoiceId: string, invoiceNumber?: string): Promise<void> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  const res = await fetch(invoicePdfUrl(invoiceId), { headers });
  if (!res.ok) {
    // Surface a friendly message — never a raw JSON/HTML error body.
    let msg = `Download failed (HTTP ${res.status}). Please try again.`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      const text = await res.text().catch(() => '');
      if (text && !text.trim().startsWith('{')) msg = text;
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = `${(invoiceNumber || invoiceId).replace(/[^a-z0-9.-]+/gi, '-')}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
}

// ── Promo Code API ──

export async function validatePromoCode(
  code: string,
  setupFeeCents?: number,
  monthlyFeeCents?: number,
  addons?: Array<{ id: string; slug?: string; setupCents: number; monthlyCents: number }>,
  // #458 — the cart's domain choice (and how much of its price the plan covers), so the server can judge a
  // free-domain code against it.
  domain?: { registering: boolean; priceCents?: number | null; includedCents?: number | null },
): Promise<ApiResponse<unknown>> {
  return request('/promo-codes/validate', {
    method: 'POST',
    body: JSON.stringify({ code, setupFeeCents, monthlyFeeCents, addons, domain }),
  });
}

// ── Admin Packages/Addons API ──

export async function getAdminPackages(): Promise<ApiResponse<unknown[]>> {
  return request('/admin/packages?includeInactive=true');
}

export async function createAdminPackage(data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request('/admin/packages', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminPackage(id: string, data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request(`/admin/packages/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAdminAddons(): Promise<ApiResponse<unknown[]>> {
  return request('/admin/addons');
}

export async function createAdminAddon(data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request('/admin/addons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAdminAddon(id: string, data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request(`/admin/addons/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAdminStaff(): Promise<ApiResponse<{ staff: Array<{ id: string; email: string; role: string }> }>> {
  return request('/admin/staff');
}

// #127 — superadmin capability matrix.
export interface CapabilityMatrix {
  capabilities: string[];
  roles: Array<{ role: string; capabilities: Record<string, { value: boolean; isDefault: boolean; default: boolean }> }>;
  superadmin: Record<string, boolean>;
  loaded: boolean;
}
export async function getRoleCapabilities(): Promise<ApiResponse<CapabilityMatrix>> {
  return request('/admin/role-capabilities');
}
export async function setRoleCapability(role: string, capability: string, allowed: boolean): Promise<ApiResponse<CapabilityMatrix>> {
  return request('/admin/role-capabilities', { method: 'PUT', body: JSON.stringify({ role, capability, allowed }) });
}

// ── Admin Orders API ──

// #349 — admin order CRUD (create / edit / cancel / resume / delete). None of these
// charges a card — see admin.routes.ts's block comment above POST /admin/orders.
export interface AdminOrderRow {
  id: string;
  client_id: string;
  package_id: string;
  package_name: string;
  client_email: string;
  business_name: string;
  status: string;
  domain: string | null;
  initial_amount_cents: number;
  monthly_amount_cents: number;
  discount_amount_cents: number;
  addon_ids: string[];
  authorize_net_transaction_id: string | null;
  authorize_net_subscription_id: string | null;
  next_billing_at: string | null;
  billing_period_end: string | null;
  cancel_reason: string | null;
  billing_failures: number;
  suspended_at: string | null;
  paid_at: string | null;
  created_at: string;
  /** True when this is the client's current (billing-eligible) order. */
  is_current: boolean;
}

export async function getAdminOrders(
  page = 1,
  pageSize = 20,
  status?: string,
): Promise<ApiResponse<AdminOrderRow[]> & { total?: number; page?: number; pageSize?: number }> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (status) params.set('status', status);
  return request<AdminOrderRow[]>(`/admin/orders?${params}`);
}

export interface CreateAdminOrderInput {
  clientId: string;
  packageId: string;
  addonIds?: string[];
  domain?: string;
  /** Integer cents; omit to let the server price it (package + add-ons). */
  monthlyAmountCents?: number;
  /** ISO date/datetime; requires a card on file or the server 400s. */
  firstBillingAt?: string;
  makeCurrent?: boolean;
}

export interface CreateAdminOrderResult {
  order: AdminOrderRow;
  isCurrent: boolean;
  billing: { startsAt: string; monthlyAmountCents: number } | null;
}

export async function createAdminOrder(data: CreateAdminOrderInput): Promise<ApiResponse<CreateAdminOrderResult>> {
  return request('/admin/orders', { method: 'POST', body: JSON.stringify(data) });
}

// Only these fields — any other key in the body is a 400 (admin.routes.ts ORDER_EDITABLE_FIELDS). Send only what
// actually changed; omit monthlyAmountCents on a package/add-on-only change so the server re-prices it.
export interface UpdateAdminOrderInput {
  packageId?: string;
  addonIds?: string[];
  monthlyAmountCents?: number;
  domain?: string;
  /** null stops automatic billing. Only accepted while the order is active/paid (409 otherwise). */
  nextBillingAt?: string | null;
}

export interface UpdateAdminOrderResult {
  order: AdminOrderRow;
  changes: string[];
  warnings: string[];
  appliesNextCycle: true;
}

export async function updateAdminOrder(orderId: string, data: UpdateAdminOrderInput): Promise<ApiResponse<UpdateAdminOrderResult>> {
  return request(`/admin/orders/${orderId}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export async function cancelAdminOrder(
  orderId: string,
  data: { reason?: string; notifyCustomer?: boolean } = {},
): Promise<ApiResponse<{ status: 'cancelling'; billingPeriodEnd: string; emailed: boolean }>> {
  return request(`/admin/orders/${orderId}/cancel`, { method: 'POST', body: JSON.stringify(data) });
}

export async function resumeAdminOrder(orderId: string): Promise<ApiResponse<{ order: AdminOrderRow; warnings: string[] }>> {
  return request(`/admin/orders/${orderId}/resume`, { method: 'POST' });
}

export async function getAdminOrderDeleteCheck(
  orderId: string,
): Promise<ApiResponse<{ deletable: boolean; blockers: string[]; confirmCode: string }>> {
  return request(`/admin/orders/${orderId}/delete-check`);
}

export async function deleteAdminOrder(
  orderId: string,
  confirm: string,
): Promise<ApiResponse<{ deleted?: true; blockers?: string[] }>> {
  return request(`/admin/orders/${orderId}`, { method: 'DELETE', body: JSON.stringify({ confirm }) });
}

// ── Admin Reporting API (QW1 usage report + QW4 expiring domains) ──

export interface UsageReportRow {
  clientId: string;
  businessName: string;
  editCount: number;
  inputTokens: number;
  outputTokens: number;
  costCents: number;
}

export interface UsageReportData {
  period: { year: number; month: number };
  rows: UsageReportRow[];
  totals: { editCount: number; inputTokens: number; outputTokens: number; costCents: number };
  averages: {
    editsPerCustomer: number;
    inputTokensPerCustomer: number;
    outputTokensPerCustomer: number;
    costCentsPerCustomer: number;
  };
}

export async function getUsageReport(year: number, month: number): Promise<ApiResponse<UsageReportData>> {
  return request(`/admin/usage?year=${year}&month=${month}`);
}

export interface ExpiringDomainRow {
  orderId: string;
  domain: string | null;
  domainExpiresAt: string | null;
  businessName: string | null;
  email: string;
}

export async function getExpiringDomains(): Promise<ApiResponse<ExpiringDomainRow[]>> {
  return request('/admin/expiring-domains');
}

// ── Admin Promo Codes API ──

export async function getAdminPromoCodes(page = 1, pageSize = 20): Promise<ApiResponse<unknown>> {
  return request(`/promo-codes/admin?page=${page}&pageSize=${pageSize}&includeInactive=true`);
}

export async function createAdminPromoCode(data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request('/promo-codes/admin', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminPromoCode(id: string): Promise<ApiResponse<unknown>> {
  return request(`/promo-codes/admin/${id}`);
}

export async function updateAdminPromoCode(id: string, data: Record<string, unknown>): Promise<ApiResponse<unknown>> {
  return request(`/promo-codes/admin/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deactivateAdminPromoCode(id: string): Promise<ApiResponse<unknown>> {
  return request(`/promo-codes/admin/${id}/deactivate`, { method: 'DELETE' });
}

export async function reactivateAdminPromoCode(id: string): Promise<ApiResponse<unknown>> {
  return request(`/promo-codes/admin/${id}/reactivate`, { method: 'POST' });
}

// ── Admin Transaction Management ──

export async function voidTransaction(transactionId: string): Promise<ApiResponse<unknown>> {
  return request(`/admin/transactions/${transactionId}/void`, { method: 'POST' });
}

// ── Admin Integration Tests ──

export async function testAuthorizeNetConnection(): Promise<ApiResponse<IntegrationTestResult>> {
  return request('/admin/test/authorizenet', { method: 'POST' });
}

// ── Admin Partners API ──

export async function getAdminPartners(): Promise<ApiResponse<unknown[]>> {
  return request('/admin/partners');
}
export async function getAdminPartner(id: string): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}`);
}
export async function getAdminPartnerRevenue(id: string): Promise<ApiResponse<unknown[]>> {
  return request(`/admin/partners/${id}/revenue`);
}
export async function getAdminPartnerClients(id: string): Promise<ApiResponse<unknown[]>> {
  return request(`/admin/partners/${id}/clients`);
}
export async function getAdminPartnerPayouts(id: string): Promise<ApiResponse<unknown[]>> {
  return request(`/admin/partners/${id}/payouts`);
}
export async function approvePartner(id: string): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}/approve`, { method: 'POST' });
}
export async function suspendPartner(id: string): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}/suspend`, { method: 'POST' });
}
export async function reactivatePartner(id: string): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}/reactivate`, { method: 'POST' });
}
export async function updatePartnerRevenueConfig(id: string, data: { revenueSharePercent?: number; setupFeeSharePercent?: number }): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}/revenue-config`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function updatePartnerAddonAccess(id: string, data: { addonId: string; enabled: boolean }): Promise<ApiResponse<unknown>> {
  return request(`/admin/partners/${id}/addon-access`, { method: 'PUT', body: JSON.stringify(data) });
}
export async function createPartnerInvite(email?: string): Promise<ApiResponse<unknown>> {
  return request('/admin/partner-invites', { method: 'POST', body: JSON.stringify({ email }) });
}
export async function getPartnerInvites(): Promise<ApiResponse<unknown[]>> {
  return request('/admin/partner-invites');
}

// ── WordPress Sync API ──

export async function syncPullFromWP(
  projectId: string,
): Promise<ApiResponse<{ pages: { slug: string; title: string; content: string; modified: string }[]; imported?: number; failed?: number }>> {
  // Bulk pull imports NEW wp-admin pages into the editor; it never overwrites
  // existing pages (task #215 — WP the_content is a lossy copy of the editor store).
  return request(`/sync/${projectId}/pull`, { method: 'POST' });
}

export async function syncPullPageFromWP(
  projectId: string,
  slug: string,
  opts?: { confirm?: boolean },
): Promise<ApiResponse<{ page?: { slug: string; title: string; content: string; modified: string }; imported?: boolean; refreshed?: boolean; slug?: string; wpTextLength?: number; legalRisk?: boolean }> & { needsConfirm?: boolean }> {
  // Bug 21.08 Issue 3: pulling an EXISTING editor page requires explicit confirmation.
  // Without opts.confirm the backend returns 409 { needsConfirm, data:{ wpTextLength,
  // legalRisk } } so the UI can warn before replacing the editor copy with WP's lossy
  // the_content (#215). New pages import without confirmation.
  return request(`/sync/${projectId}/pull/${slug}`, {
    method: 'POST',
    body: JSON.stringify({ confirm: opts?.confirm === true }),
  });
}

export async function syncPushToWP(projectId: string, slug: string, data?: { html?: string; title?: string }): Promise<ApiResponse<unknown>> {
  return request(`/sync/${projectId}/push/${slug}`, {
    method: 'POST',
    body: data ? JSON.stringify(data) : undefined,
  });
}

export async function syncPushAllToWP(projectId: string): Promise<ApiResponse<{ results: { slug: string; success: boolean; error?: string }[] }>> {
  return request(`/sync/${projectId}/push-all`, { method: 'POST' });
}

export async function saveSyncPage(projectId: string, slug: string, html: string, title?: string): Promise<ApiResponse<void>> {
  return request(`/sync/${projectId}/pages/${slug}`, {
    method: 'PUT',
    body: JSON.stringify({ html, title }),
  });
}

export async function getWPSyncStatus(projectId: string): Promise<ApiResponse<{ status: string; version: string; wordpress: string; lastSyncAt: string | null }>> {
  return request(`/sync/${projectId}/status`);
}

// #397: both routes answer success:true even for a page they skipped as
// 'live-content-diverged' (the owner edited it in WordPress since our last
// publish) — the skip is reported only in `data.warnings`, which nothing
// read. `opts.overrideDiff` is the documented bypass (sync.routes.ts
// shouldSkipPublishForDiff); packages/frontend/src/utils/publishDiverged.ts
// is what actually decides when to send it.
export async function ftpPublishPage(
  projectId: string,
  slug: string,
  data: { html: string; title?: string },
  opts?: { overrideDiff?: boolean },
): Promise<ApiResponse<{ slug: string; method: string; warnings?: Array<{ slug: string; reason: string }> }>> {
  return request(`/sync/${projectId}/ftp-push/${slug}`, {
    method: 'POST',
    body: JSON.stringify({ ...data, overrideDiff: opts?.overrideDiff === true }),
  });
}

export async function ftpPublishAll(
  projectId: string,
  opts?: { overrideDiff?: boolean },
): Promise<ApiResponse<{ results: { slug: string; success: boolean; error?: string }[]; warnings?: Array<{ slug: string; reason: string }> }>> {
  return request(`/sync/${projectId}/ftp-push-all`, {
    method: 'POST',
    body: JSON.stringify({ overrideDiff: opts?.overrideDiff === true }),
  });
}

/** One-time WordPress admin login link (POST /api/sync/:id/wp-admin-login). The route answers `{ success, loginUrl }`
 *  at the top level (no `data` envelope); `error` carries its friendly message (e.g. the site isn't deployed yet). */
export async function getWpAdminLoginUrl(projectId: string): Promise<ApiResponse<unknown> & { loginUrl?: string }> {
  return request(`/sync/${projectId}/wp-admin-login`, { method: 'POST' });
}

// ── Image API ──

export async function uploadImage(siteId: string, file: File): Promise<ApiResponse<UploadedImage>> {
  const formData = new FormData();
  formData.append('image', file);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }
  // Do NOT set Content-Type — browser will set multipart boundary automatically

  const res = await fetch(`${API_BASE}/images/${siteId}/images`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      return uploadImage(siteId, file);
    }
    // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
    // gated — see handleSessionExpired); this request still fails either way.
    throw new Error('Session expired');
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return { success: false, error: `Upload failed with status ${res.status}. The server returned an unexpected response.` };
  }

  const data = await res.json();
  if (!res.ok && !data.error) {
    data.error = `Upload failed with status ${res.status}`;
  }
  return data;
}

export async function getImages(siteId: string): Promise<ApiResponse<UploadedImage[]>> {
  return request(`/images/${siteId}/images`);
}

export async function deleteImage(siteId: string, imageId: string): Promise<ApiResponse<void>> {
  return request(`/images/${siteId}/images/${imageId}`, { method: 'DELETE' });
}

// ── Project Wizard API ──

export interface ProductCategoryConfig {
  name: string;
  slug: string;
  description?: string;
  parent?: string;
}

export interface ProductFilterConfig {
  name: string;
  slug: string;
  type: 'select' | 'color' | 'label';
  options: string[];
}

export interface EcommerceConfig {
  categories: ProductCategoryConfig[];
  filters: ProductFilterConfig[];
}

export interface ProjectData {
  id: string;
  clientId: string;
  orderId?: string;
  domain: string;
  sitemap: { slug: string; title: string; menuOrder: number; isHomepage?: boolean }[];
  colors: { primary: string; secondary: string; accent: string; background: string; text: string };
  logoPath?: string;
  isEcommerce: boolean;
  ecommerceConfig?: EcommerceConfig;
  contentBrief?: string;
  brandFeel?: string;
  // #SEO-2 — optional wizard-set vertical/category (e.g. "hvac contractor").
  // Persists to projects.business_category; deploy-time LocalBusiness schema
  // prefers this over the LLM-derive fallback when set.
  businessCategory?: string;
  contactEmail?: string;
  contactSuccessMessage?: string;
  status: string;
  twentyiPackageId?: string;
  // #76/#77: the package ACTUALLY serving this project right now (prefers
  // staging_package_id while status is staging/deployed_staging, otherwise
  // twentyiPackageId) — use this, not twentyiPackageId, to gate "is this
  // project served" UI (Domain tab / WP-edit / go-live messaging).
  servingPackageId?: string;
  // servingUrl = deployUrl || stagingUrl, whichever URL is actually live.
  servingUrl?: string;
  // #852: lets the Web address tab give BYO custom-domain projects DNS
  // guidance before the production package exists.
  domainKind?: 'subdomain' | 'custom';
  deployUrl?: string;
  dnsInfo?: { nameservers?: string[]; aRecord?: string; cnameTarget?: string; configured: boolean; message: string; reason?: string };
  stagingUrl?: string;
  deployTarget?: 'production' | 'staging';
  error?: string;
  needsFullPublishAt?: string;
  lastSyncAt?: string;
  // #89 — ISO timestamp the owner accepted the AI-legal-content disclaimer.
  // Undefined = not yet accepted; the publish flow prompts for consent before
  // deploy when SF_LEGAL_CONSENT_GATE is on. Set via POST /projects/:id/legal-consent.
  legalDisclaimerAcceptedAt?: string;
  // Phase E — canonical env (SF_CANONICAL_ENV). canonicalEnvMismatch is derived
  // server-side (marker set AND != the serving env); the editor shows a warning.
  canonicalEnv?: string;
  canonicalEnvMismatch?: boolean;
  ageGateEnabled?: boolean;
  ageGateMinAge?: number;
  ageGateMessage?: string;
  portfolioSettings?: PortfolioSettings;
  // Phase 1D — Settings tab fields
  contactPhone?: string;
  contactAddress?: string;
  businessHours?: Record<string, unknown>;
  googleAnalyticsId?: string;
  createdAt: string;
  updatedAt: string;
  pages?: { id: string; slug: string; title: string; htmlPath: string; menuOrder: number }[];
}

export interface PortfolioSettings {
  hideViewDetails: boolean;
  disableLink: boolean;
  multiImageEnabled: boolean;
  sidebarPosition: 'left' | 'right' | 'none';
}

export const DEFAULT_PORTFOLIO_SETTINGS: PortfolioSettings = {
  hideViewDetails: false,
  disableLink: false,
  multiImageEnabled: false,
  sidebarPosition: 'none',
};

export interface DeploymentData {
  id: string;
  projectId: string;
  clientId: string;
  status: string;
  deployUrl?: string;
  dnsInfo?: ProjectData['dnsInfo'];
  error?: string;
  log: { step: string; status: string; message?: string; timestamp: string }[];
  progressPercent: number;
  totalSteps: number;
  completedSteps: number;
  createdAt: string;
  completedAt?: string;
}

export async function listProjects(): Promise<ApiResponse<ProjectData[]>> {
  return request('/projects');
}

export async function getProjectCount(): Promise<ApiResponse<{ count: number }>> {
  return request('/projects/count');
}

export async function getProject(projectId: string): Promise<ApiResponse<ProjectData>> {
  return request(`/projects/${projectId}`);
}

export async function deleteProject(projectId: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}`, { method: 'DELETE' });
}

export async function createProject(data: {
  domain: string;
  sitemap: { slug: string; title: string; menuOrder: number; isHomepage?: boolean }[];
  colors: { primary: string; secondary: string; accent: string; background: string; text: string };
  isEcommerce: boolean;
  contentBrief?: string;
  brandFeel?: string;
  businessCategory?: string;
}): Promise<ApiResponse<{ id: string }>> {
  return request('/projects', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateProject(projectId: string, data: {
  sitemap?: ProjectData['sitemap'];
  colors?: ProjectData['colors'];
  isEcommerce?: boolean;
  ecommerceConfig?: EcommerceConfig;
  contentBrief?: string;
  brandFeel?: string;
  businessCategory?: string;
  domain?: string;
  contactEmail?: string;
  contactSuccessMessage?: string;
  importedContent?: { posts: WebsiteAnalysis['blogPosts']; products: WebsiteAnalysis['products']; testimonials?: WebsiteAnalysis['testimonials'] };
  ageGateEnabled?: boolean;
  ageGateMinAge?: number;
  ageGateMessage?: string;
  portfolioSettings?: PortfolioSettings;
}): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Authenticated multipart POST with automatic token refresh on 401.
// Plain `fetch` callers like uploadLogo can't use request() (which assumes
// JSON bodies), so we mirror its refresh logic for FormData uploads.
async function authedMultipartPost<T>(path: string, formData: FormData): Promise<ApiResponse<T>> {
  const doFetch = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: formData });
  };

  let res = await doFetch(accessToken);

  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      res = await doFetch(newAccess);
    } else {
      // #2189 — this used to unconditionally setTokens(null) here, which could wipe a
      // NEWER session set concurrently while this refresh was in flight. refreshOnce()
      // already ran the (generationally gated) session-expired flow itself; just report
      // this request's own failure.
      return { success: false, error: 'Your session has expired. Please sign in again.' };
    }
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    if (res.status === 401 || res.status === 403) {
      return { success: false, error: 'Your session has expired. Please refresh the page and sign in again.' };
    }
    return { success: false, error: `Upload failed with status ${res.status}. Please try again.` };
  }
  const data = (await res.json()) as ApiResponse<T>;
  if (!res.ok && !(data as any).error) {
    (data as any).error = res.status === 401 || res.status === 403
      ? 'Your session has expired. Please refresh the page and sign in again.'
      : `Upload failed (${res.status}). Please try again.`;
  }
  return data;
}

export async function uploadLogo(projectId: string, file: File): Promise<ApiResponse<{ logoPath: string }>> {
  const formData = new FormData();
  formData.append('logo', file);
  return authedMultipartPost<{ logoPath: string }>(`/projects/${projectId}/logo`, formData);
}

export async function applyLogoToAllPages(projectId: string): Promise<ApiResponse<{ pagesUpdated: number; pagesSkipped: number; totalPages: number; totalImgsSwapped: number; logoUrl: string | null }>> {
  return request(`/projects/${projectId}/logo/apply`, { method: 'POST' });
}

export async function uploadPageHtml(projectId: string, slug: string, file: File): Promise<ApiResponse<{ slug: string; htmlPath: string }>> {
  const formData = new FormData();
  formData.append('html', file);

  // #1390 — route the editor SAVE through authedFetch (401 -> single-flight refresh -> retry -> else
  // session-expired) instead of a bare fetch that silently failed on token expiry mid-edit, letting the
  // user keep editing with no refresh and no session-expired state (the #1117 raw-Response pattern).
  const res = await authedFetch(`/projects/${projectId}/pages/${slug}/html`, {
    method: 'POST',
    body: formData,
  });

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return { success: false, error: `Upload failed with status ${res.status}` };
  }
  return res.json();
}

export async function getProjectPages(projectId: string): Promise<ApiResponse<{
  id: string; slug: string; title: string; htmlPath: string; menuOrder: number; hasUnpublishedChanges?: boolean;
  // #2321/#2325 — source/regens_used/system_role straight off the project_pages row (wire format,
  // not camelCased — see project.routes.ts's GET /:id/pages SELECT); countsTowardLimit is computed
  // server-side (page-count.service.ts) so the frontend never re-derives the rule and drifts from it.
  source?: string | null; regens_used?: number; system_role?: string | null; countsTowardLimit?: boolean;
}[]>> {
  return request(`/projects/${projectId}/pages`);
}

// #6 (#50, BR10.07) — rename a page's title and/or slug. The backend slugifies + auto-uniquifies
// the requested slug, cascades all stores + sibling links, and flags a pending re-publish.
export async function renamePage(
  projectId: string,
  oldSlug: string,
  body: { title?: string; slug?: string },
): Promise<ApiResponse<{ slug: string; title: string; slugChanged: boolean; requestedSlug: string; cascadedPages: number; liveRename?: 'done' | 'pending' }>> {
  return request(`/projects/${projectId}/pages/${oldSlug}`, { method: 'PATCH', body: JSON.stringify(body) });
}

export async function getPageHtml(projectId: string, slug: string): Promise<ApiResponse<{ slug: string; html: string }>> {
  return request(`/projects/${projectId}/pages/${slug}/html?_t=${Date.now()}`);
}

// #2322 — delete a page. Legal-template pages (privacy/terms) require an explicit confirmation
// first — pass confirmLegal once the user has checked the "legal pages are often required" box,
// otherwise the backend 409s with code LEGAL_CONFIRM_REQUIRED. The home page and WordPress-owned
// system pages always 400.
export async function deletePage(
  projectId: string,
  slug: string,
  opts?: { confirmLegal?: boolean },
): Promise<ApiResponse<{ slug: string; liveTrash: 'done' | 'pending' | 'skipped' }>> {
  const qs = opts?.confirmLegal ? '?confirm=legal' : '';
  return request(`/projects/${projectId}/pages/${slug}${qs}`, { method: 'DELETE' });
}

// #43 (BR9.07-6) — poll an in-flight interactive edit's generation_runs state after
// the SSE relay dropped (CONNECTION_LOST). Returns { success, state, done }; the FE
// re-fetches the page once state==='completed' (or done+unknown) instead of showing
// a false "failed" while the edit finishes in the background.
export async function getEditRunStatus(
  projectId: string,
  slug: string,
  runId: string,
): Promise<{ success: boolean; state?: string; done?: boolean; error?: string }> {
  return request(`/projects/${projectId}/pages/${slug}/run-status/${runId}`) as unknown as {
    success: boolean; state?: string; done?: boolean; error?: string;
  };
}

export interface InflightRun {
  id: string;
  pageSlug: string;
  status: 'started' | 'running' | 'failed' | 'abandoned';
  stage: string | null;
  kind: string;
  startedAt: string;
  // GP-1: present (when SF_RUN_STATUS_HONEST on) for runs that just
  // failed/abandoned, so the wizard can show a clear error not a silent reset.
  error?: string | null;
}

// Surfaces generation runs still in progress on this project so the
// wizard can show the spinner + status text on mount instead of a stale
// "Create this page" button (which would dedup-collide with the
// already-running gen and confuse the customer).
export async function getInflightRuns(projectId: string): Promise<ApiResponse<InflightRun[]>> {
  return request(`/projects/${projectId}/inflight-runs?_t=${Date.now()}`);
}

// ── Dashboard API ──

export async function getDashboardOverview(projectId?: string): Promise<ApiResponse<any>> {
  return request(projectId ? `/dashboard/overview?projectId=${encodeURIComponent(projectId)}` : '/dashboard/overview');
}

export async function refreshSpeedScore(): Promise<ApiResponse<any>> {
  return request('/dashboard/speed-score/refresh', { method: 'POST' });
}

// MGR-009 — per-project refresh used by ManagePage
export async function refreshSpeedScoreForProject(projectId: string): Promise<ApiResponse<any>> {
  return request(`/dashboard/speed-score/refresh/${projectId}`, { method: 'POST' });
}

export async function getSpeedScoreForProject(projectId: string): Promise<ApiResponse<{ mobileScore: number | null; desktopScore: number | null; fetchedAt: string } | null>> {
  return request(`/dashboard/speed-score/${projectId}`);
}

// ── Style Recommendations ──

export async function recommendStyle(businessDescription: string, brandFeel?: string): Promise<ApiResponse<{ colors: ColorScheme; styleDirection: string; fontPair: string; rationale: string }>> {
  return request('/projects/recommend-style', {
    method: 'POST',
    body: JSON.stringify({ businessDescription, brandFeel }),
  });
}

export async function getPageUsage(projectId: string, slug: string): Promise<ApiResponse<{ regensUsed: number; maxRegens: number; editsUsed: number; maxEdits: number | null }>> {
  return request(`/projects/${projectId}/pages/${slug}/usage`);
}

// Bug #36 — persisted per-page undo/version store (project_page_versions).
// Backs the editor's "survive a refresh" Undo: ManagePage seeds its local
// undoHistory from this when empty (e.g. after a refresh with no localStorage
// history). Most-recent-first, capped at 10 server-side.
export interface PageVersionSummary {
  id: string;
  label: string | null;
  createdAt: string;
}

export async function getPageVersions(projectId: string, slug: string): Promise<ApiResponse<PageVersionSummary[]>> {
  return request(`/projects/${projectId}/pages/${slug}/versions`);
}

export async function getPageVersion(projectId: string, slug: string, versionId: string): Promise<ApiResponse<{ html: string }>> {
  return request(`/projects/${projectId}/pages/${slug}/versions/${versionId}`);
}

export async function buildProject(projectId: string, deployTarget?: 'production' | 'staging'): Promise<ApiResponse<DeploymentData>> {
  return request(`/projects/${projectId}/build`, {
    method: 'POST',
    body: JSON.stringify({ deployTarget: deployTarget || 'production' }),
  });
}

// Bug 11.05/3 — "Take it down temporarily" now goes through a dedicated
// endpoint instead of trying to slip a status field through the generic
// PUT (which silently dropped it). republish reverses the action.
export async function takedownProject(projectId: string): Promise<ApiResponse<{ message: string }>> {
  return request(`/projects/${projectId}/takedown`, { method: 'POST' });
}
export async function republishProject(projectId: string): Promise<ApiResponse<{ message: string }>> {
  return request(`/projects/${projectId}/republish`, { method: 'POST' });
}

export async function promoteToProduction(projectId: string): Promise<ApiResponse<DeploymentData>> {
  return request(`/projects/${projectId}/promote`, { method: 'POST' });
}

export async function getDeployment(projectId: string, deploymentId: string): Promise<ApiResponse<DeploymentData>> {
  return request(`/projects/${projectId}/deployments/${deploymentId}`);
}

export async function listDeployments(projectId: string): Promise<ApiResponse<DeploymentData[]>> {
  return request(`/projects/${projectId}/deployments`);
}

export interface SslStatus {
  ready: boolean;
  domain: string;
  tempPreviewUrl: string | null;
  provisionedAt?: string;
  cert?: { cn: string | null; sans: string[]; validUntil: string | null };
  defaultStackCpCert?: boolean;
}
export async function getSslStatus(projectId: string): Promise<ApiResponse<SslStatus>> {
  return request(`/projects/${projectId}/ssl-status`);
}

// ── Project Images API ──

export interface ProjectImage {
  id: string;
  projectId: string;
  clientId: string;
  pageSlug?: string;
  originalName: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  width: number;
  height: number;
  altText?: string;
  needsBgRemoval: boolean;
  bgRemoved: boolean;
  bgRemovedPath?: string;
  aiDescription?: string;
  url: string;
  createdAt: string;
}

export async function uploadProjectImage(
  projectId: string,
  file: File,
  pageSlug?: string,
  altText?: string,
  /**
   * Task #562 — optional image_role tag for the new project_images row.
   * 'user_library' marks photos the customer bulk-uploaded as their own
   * stock library (Site Photos in the wizard). The slot-intent-resolver
   * preferences these over scraped / stock fallbacks.
   */
  imageRole?: string,
): Promise<ApiResponse<ProjectImage>> {
  const formData = new FormData();
  formData.append('image', file);
  if (pageSlug) formData.append('pageSlug', pageSlug);
  if (altText) formData.append('altText', altText);
  if (imageRole) formData.append('imageRole', imageRole);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}/projects/${projectId}/images`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      return uploadProjectImage(projectId, file, pageSlug, altText, imageRole);
    }
    // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
    // gated — see handleSessionExpired); this request still fails either way.
    throw new Error('Session expired');
  }

  const contentType = res.headers.get('content-type') || '';
  if (!contentType.includes('application/json')) {
    return { success: false, error: `Upload failed with status ${res.status}` };
  }
  return res.json();
}

export async function getProjectImages(
  projectId: string,
  pageSlug?: string,
): Promise<ApiResponse<ProjectImage[]>> {
  const params = pageSlug ? `?page=${encodeURIComponent(pageSlug)}` : '';
  return request(`/projects/${projectId}/images${params}`);
}

export async function deleteProjectImage(
  projectId: string,
  imageId: string,
): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/images/${imageId}`, { method: 'DELETE' });
}

// ── #1296 Custom fonts (gated by VITE_SF_CUSTOM_FONTS / backend SF_CUSTOM_FONTS) ──

export interface ProjectFont {
  id: string;
  familyName: string;
  format: string;
  fileSize?: number;
  url: string;
  createdAt: string;
}

/** Upload a custom font file (woff/woff2/ttf/otf, <=5MB) for a project. */
export async function uploadProjectFont(
  projectId: string,
  file: File,
  familyName?: string,
): Promise<ApiResponse<ProjectFont>> {
  const formData = new FormData();
  formData.append('font', file);
  if (familyName) formData.append('familyName', familyName);
  return authedMultipartPost<ProjectFont>(`/projects/${projectId}/fonts`, formData);
}

export async function getProjectFonts(
  projectId: string,
): Promise<ApiResponse<ProjectFont[]>> {
  return request(`/projects/${projectId}/fonts`);
}

export async function deleteProjectFont(
  projectId: string,
  fontId: string,
): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/fonts/${fontId}`, { method: 'DELETE' });
}

// ── #1294 Brand-kit auto-extract (gated by VITE_SF_BRAND_AUTOEXTRACT / backend SF_BRAND_AUTOEXTRACT) ──

export interface ExtractedBrand {
  colors: { primary?: string; secondary?: string; accent?: string; background?: string; text?: string; palette?: string[] };
  palette?: string[];
  fonts: { primary?: string; secondary?: string };
}

/** Auto-extract brand colors + fonts from an uploaded brand-kit file (PDF or image). */
export async function extractBrand(
  projectId: string,
  file: File,
): Promise<ApiResponse<ExtractedBrand>> {
  const formData = new FormData();
  formData.append('file', file);
  return authedMultipartPost<ExtractedBrand>(`/projects/${projectId}/extract-brand`, formData);
}

/**
 * Phase 2.3 — "Start over" affordance. Roll back ALL edits made during the
 * current editing session (since the first user chat turn after 30+ min
 * idle) to the snapshot taken at session start. Returns 400 with a friendly
 * message when no checkpoint exists yet (first edit not made / page never
 * edited).
 */
export async function resetPageToSessionStart(
  projectId: string,
  slug: string,
): Promise<ApiResponse<{ restored: boolean; sessionStartAt: string | null }>> {
  return request(`/projects/${projectId}/pages/${slug}/reset-to-session-start`, { method: 'POST' });
}

export async function removeImageBackground(
  projectId: string,
  imageId: string,
): Promise<ApiResponse<ProjectImage>> {
  return request(`/projects/${projectId}/images/${imageId}/remove-bg`, { method: 'POST' });
}

/**
 * Crop (and optionally resize) an existing project image into a NEW asset.
 * `crop` is a pixel rectangle in the SOURCE image's coordinates (as produced
 * by react-easy-crop's croppedAreaPixels). Returns the new image.
 */
export async function cropProjectImage(
  projectId: string,
  imageId: string,
  crop: { x: number; y: number; width: number; height: number },
  resizeTo?: { width: number; height: number } | null,
): Promise<ApiResponse<ProjectImage>> {
  return request(`/projects/${projectId}/images/${imageId}/crop`, {
    method: 'POST',
    body: JSON.stringify({ crop, resizeTo: resizeTo || null }),
  });
}

// ── Stock Photos (Pexels) ──

export interface StockPhotoResult {
  id: number;
  thumbnail: string;
  fullUrl: string;
  originalUrl: string;
  photographer: string;
  width: number;
  height: number;
  attribution: string;
}

export async function searchStockPhotos(
  projectId: string,
  query: string,
  page = 1,
): Promise<ApiResponse<{ results: StockPhotoResult[]; totalResults: number; page: number }>> {
  return request(`/projects/${projectId}/stock-images?q=${encodeURIComponent(query)}&page=${page}`);
}

export async function downloadStockPhoto(
  projectId: string,
  pexelsUrl: string,
  photographer: string,
  pageSlug?: string,
): Promise<ApiResponse<ProjectImage>> {
  return request(`/projects/${projectId}/stock-images/download`, {
    method: 'POST',
    body: JSON.stringify({ pexelsUrl, photographer, pageSlug }),
  });
}

export async function downloadExternalImage(
  projectId: string,
  imageUrl: string,
): Promise<ApiResponse<ProjectImage>> {
  return request(`/projects/${projectId}/stock-images/download`, {
    method: 'POST',
    body: JSON.stringify({ pexelsUrl: imageUrl, photographer: 'Source Website' }),
  });
}

// ── AI Image Generation (DALL-E / gpt-image-2) ──

/** AI image quota response from GET /:id/ai-image-quota. */
export interface AiImageQuota {
  enabled: boolean;
  reason: 'ok' | 'free-trial' | 'admin-disabled';
  used: number;
  cap: number;
  period: 'month';
  /** ISO timestamp marking the first instant of next calendar month (UTC). */
  resetsAt: string;
  perMinuteRemaining: number;
  /** Path the upsell CTA should navigate to for free-trial clients. */
  upgradeUrl: string;
}

export type AiImageSize = '1024x1024' | '1792x1024' | '1024x1792';

/** Read the calling client's AI image entitlement + monthly usage. */
export async function getAiImageQuota(
  projectId: string,
): Promise<ApiResponse<AiImageQuota>> {
  return request(`/projects/${projectId}/ai-image-quota`);
}

/**
 * Generate one AI image from a text prompt. Returns the same shape as
 * uploadProjectImage so callers can hand the result straight to
 * setPendingImage().
 */
export async function generateAiImage(
  projectId: string,
  prompt: string,
  opts?: { size?: AiImageSize; pageSlug?: string },
): Promise<ApiResponse<ProjectImage>> {
  return request(`/projects/${projectId}/images/generate`, {
    method: 'POST',
    body: JSON.stringify({
      prompt,
      size: opts?.size,
      pageSlug: opts?.pageSlug,
    }),
  });
}

// ── Video Upload ──

export interface ProjectVideo {
  id: string;
  project_id: string;
  client_id: string;
  page_slug: string | null;
  original_name: string;
  storage_path: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export async function uploadProjectVideo(
  projectId: string,
  file: File,
  pageSlug?: string,
  onProgress?: (percent: number) => void,
): Promise<ApiResponse<ProjectVideo>> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('video', file);
    if (pageSlug) formData.append('pageSlug', pageSlug);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.addEventListener('load', () => {
      try {
        const res = JSON.parse(xhr.responseText);
        resolve(res);
      } catch {
        resolve({ success: false, error: 'Upload failed' });
      }
    });

    xhr.addEventListener('error', () => resolve({ success: false, error: 'Upload failed. Please check your internet connection.' }));
    xhr.addEventListener('abort', () => resolve({ success: false, error: 'Upload cancelled' }));

    xhr.open('POST', `${API_BASE}/projects/${projectId}/videos`);
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.send(formData);
  });
}

export async function getProjectVideos(projectId: string): Promise<ApiResponse<ProjectVideo[]>> {
  return request(`/projects/${projectId}/videos`);
}

export async function deleteProjectVideo(projectId: string, videoId: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/videos/${videoId}`, { method: 'DELETE' });
}

// ── #52 Media library (downloadable PDFs #50 + placeable short videos #51) ──
export interface ProjectMedia {
  id: string;
  kind: 'pdf' | 'video';
  title: string;
  originalName?: string;
  mimeType?: string;
  sizeBytes?: number;
  /** Relative serve URL — for same-origin display inside the editor. */
  url: string;
  /** Absolute serve URL — what gets written into page.html (resolves cross-origin). */
  absoluteUrl: string;
  /** Absolute URL that forces a Save-As download (used by PDF download buttons). */
  downloadUrl: string;
  createdAt: string;
}

export async function uploadProjectMedia(
  projectId: string,
  file: File,
  title?: string,
  onProgress?: (percent: number) => void,
): Promise<ApiResponse<ProjectMedia>> {
  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });
    }

    xhr.addEventListener('load', () => {
      try {
        resolve(JSON.parse(xhr.responseText));
      } catch {
        resolve({ success: false, error: 'Upload failed' });
      }
    });
    xhr.addEventListener('error', () => resolve({ success: false, error: 'Upload failed. Please check your internet connection.' }));
    xhr.addEventListener('abort', () => resolve({ success: false, error: 'Upload cancelled' }));

    xhr.open('POST', `${API_BASE}/projects/${projectId}/media`);
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    xhr.send(formData);
  });
}

export async function getProjectMedia(projectId: string, kind?: 'pdf' | 'video'): Promise<ApiResponse<ProjectMedia[]>> {
  return request(`/projects/${projectId}/media${kind ? `?kind=${kind}` : ''}`);
}

export async function deleteProjectMedia(projectId: string, mediaId: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/media/${mediaId}`, { method: 'DELETE' });
}

// ── URL Analysis ──

export interface WebsiteAnalysis {
  domain: string;
  sitemap: { slug: string; title: string; menuOrder: number; isHomepage?: boolean }[];
  colors: { primary: string; secondary: string; accent: string; background: string; text: string };
  /** #806 — the pre-advice extracted palette, present ONLY when
   *  aiColorRecommend was set and the advisor changed the colors. Powers the
   *  wizard's "Revert to extracted colors" link. */
  extractedColors?: { primary: string; secondary: string; accent: string; background: string; text: string };
  businessName: string;
  businessType: string;
  summary: string;
  recommendedStyle?: string;
  // Pre-fill data extracted from source website
  sourceImages?: Array<{ url: string; alt: string }>;
  contactInfo?: { email?: string; phone?: string; address?: string };
  aboutText?: string;
  socialLinks?: { facebook?: string; instagram?: string; linkedin?: string; twitter?: string; youtube?: string };
  logoUrl?: string | null;
  fonts?: { primary?: string; secondary?: string; googleFontsUrl?: string };
  // Imported content from source site
  blogPosts?: Array<{ title: string; excerpt: string; content: string; date: string; author: string; featuredImage?: string; categories: string[]; sourceUrl: string }>;
  products?: Array<{ name: string; price: string; description: string; images: string[]; categories: string[]; sourceUrl: string }>;
  testimonials?: Array<{ author: string; text: string; rating?: number; role?: string; company?: string; avatarUrl?: string; sourceUrl: string }>;
  cptPosts?: Record<string, Array<{ title: string; slug: string; content: string; excerpt?: string; featuredImage?: string; date?: string; sourceUrl: string }>>;
  /**
   * #1581 — retail/online-store detect + upsell (staging-only, SF_RETAIL_UPSELL).
   * isRetail is the raw deterministic-detector signal (platform fingerprint,
   * product schema, or 2+ behavioral signals — see detectRetailSource on the
   * backend). retailUpsellEligible additionally requires the flag to be ON —
   * ONLY that field should drive the wizard's non-blocking upsell banner.
   */
  isRetail?: boolean;
  retailSignals?: string[];
  retailUpsellEligible?: boolean;
  /**
   * m31 P5 (I1, #2429) — true when the source site is THIN: a short about text (< ~300 chars) on ≤ 2 parsed pages, or no
   * services detected (isSourceThin on the backend). The wizard's "Tell us about your business" step then asks for three
   * optional facts. Absent on a result cached before the flag existed — read it as `=== true`, never as "unknown means thin".
   */
  sourceThin?: boolean;
}

export async function analyzeUrl(
  url: string,
  onStatus?: (message: string) => void,
  onEstimate?: (seconds: number) => void,
  // #806 — opt-in brand-anchored AI palette recommendation. When true the
  // backend runs the palette advisor over the extracted colors and the `done`
  // payload's `colors` are the advised set, with the pre-advice set echoed in
  // `extractedColors` (only when the advisor changed them) for instant revert.
  options?: { aiColorRecommend?: boolean },
): Promise<ApiResponse<WebsiteAnalysis>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}/projects/analyze-url`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ url, ...(options?.aiColorRecommend ? { aiColorRecommend: true } : {}) }),
  });

  // Bug 5 (5.06): the backend short-circuits with JSON BEFORE switching to
  // SSE (the 429 rate limiter, missing clientId/url 400s). The reader below
  // finds no SSE lines in a JSON body and surfaced the placeholder
  // "Stream ended unexpectedly" to the user, swallowing the friendly message
  // ("Too many URL analysis requests…"). Same pre-check as
  // getConsolidationRecommendations (Postfix bug 4, 20.05 followup).
  if (!res.ok || !(res.headers.get('content-type') || '').includes('text/event-stream')) {
    try {
      const body = await res.json();
      const friendly = (body && (body.error || body.message))
        || 'We couldn\'t analyze that website. Please try again in a moment.';
      return { success: false, error: friendly, code: body?.code };
    } catch {
      return { success: false, error: 'We couldn\'t analyze that website. Please try again in a moment.' };
    }
  }

  if (!res.body) return { success: false, error: 'No response body' };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  // Belt-and-braces (5.06): even if a true SSE stream dies without a terminal
  // event, never show the raw sentinel — keep the fallback user-friendly.
  let result: ApiResponse<WebsiteAnalysis> = { success: false, error: 'We couldn\'t analyze that website. Please try again in a moment.' };
  let eventType = '';

  const processLines = (lines: string[]) => {
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (eventType === 'status' && onStatus) {
          try { const parsed = JSON.parse(data); onStatus(parsed.message); } catch { /* skip */ }
        } else if (eventType === 'estimate' && onEstimate) {
          try { const parsed = JSON.parse(data); onEstimate(parsed.seconds); } catch { /* skip */ }
        } else if (eventType === 'done') {
          try { result = { success: true, data: JSON.parse(data) }; } catch { result = { success: false, error: 'Failed to parse response' }; }
        } else if (eventType === 'error') {
          // Same code-passthrough fix as the HTTP-level error path above.
          try { const errData = JSON.parse(data); result = { success: false, error: errData.error || 'Unknown error', code: errData.code }; } catch { result = { success: false, error: data }; }
        }
        eventType = '';
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    processLines(lines);
  }
  if (buffer.trim()) {
    processLines(buffer.split('\n'));
  }
  return result;
}

// ── Website Content Summarization ──

export async function summarizeWebsiteContent(url: string): Promise<ApiResponse<{ summary: string }>> {
  return request('/projects/summarize-content', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

// ── AI Page Consolidation Recommendations ──

export interface ConsolidationRecommendation {
  targetSlug: string;
  targetTitle: string;
  sourceUrls: string[];
  mergedSlugs: string[];
  mergedTitles: string[];
  rationale: string;
}

export interface ConsolidationPlan {
  recommendations: ConsolidationRecommendation[];
  resultingSitemap: { slug: string; title: string; menuOrder: number; isHomepage?: boolean; sourceUrls?: string[] }[];
  summary: string;
}

export async function getConsolidationRecommendations(
  projectId: string,
  data: {
    domain: string;
    sitemap: { slug: string; title: string; menuOrder: number; isHomepage?: boolean }[];
    maxPages: number;
    sourceUrlMap?: Record<string, string>;
  },
  onStatus?: (message: string) => void,
): Promise<ApiResponse<ConsolidationPlan>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}/projects/${projectId}/consolidation-recommendations`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });

  // Postfix bug 4 (20.05 followup): the backend short-circuits with a JSON
  // 4xx/5xx (e.g. "Page count is already within your plan limit") BEFORE
  // switching to SSE. The previous code unconditionally entered the SSE
  // reader, found no `event: done` / `event: error` lines in the JSON body,
  // and surfaced the placeholder "Stream ended unexpectedly" string to the
  // user. Now: if the response is not ok OR the content-type isn't SSE,
  // parse the JSON body and return the friendly error directly.
  if (!res.ok || !(res.headers.get('content-type') || '').includes('text/event-stream')) {
    try {
      const body = await res.json();
      const friendly = (body && (body.error || body.message))
        || 'We couldn\'t prepare a consolidation plan. Please try again in a moment.';
      return { success: false, error: friendly, code: body?.code };
    } catch {
      return {
        success: false,
        error: 'We couldn\'t prepare a consolidation plan. Please try again in a moment.',
      };
    }
  }

  if (!res.body) return { success: false, error: 'No response body' };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ApiResponse<ConsolidationPlan> = { success: false, error: 'Stream ended unexpectedly' };
  let eventType = '';

  const processLines = (lines: string[]) => {
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const d = line.slice(6);
        if (eventType === 'status' && onStatus) {
          try { const parsed = JSON.parse(d); onStatus(parsed.message); } catch { /* skip */ }
        } else if (eventType === 'done') {
          try { result = { success: true, data: JSON.parse(d) }; } catch { result = { success: false, error: 'Failed to parse response' }; }
        } else if (eventType === 'error') {
          try { const errData = JSON.parse(d); result = { success: false, error: errData.error || 'Unknown error' }; } catch { result = { success: false, error: d }; }
        }
        eventType = '';
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    processLines(lines);
  }
  if (buffer.trim()) {
    processLines(buffer.split('\n'));
  }
  return result;
}

// ── AI Page Generation ──

export interface GeneratePageResult {
  slug: string;
  title: string;
  htmlPath: string;
  html: string;
  assistantMessage: string;
  // #961 — intent-match gate-2 note. Present only when SF_INTENT_VERIFY is on
  // AND a subjective edit was judged a mismatch; rendered as a second 'help'
  // chat bubble. The `done` SSE payload is passed through wholesale, so this
  // flows to callers automatically once declared here.
  advisory?: string;
  tokensUsed?: { input: number; output: number };
}

// ── Local WordPress Deploy API ──

export interface LocalDeployResult {
  url: string;
  adminUrl: string;
  themeName: string;
  credentials: { username: string; password: string };
}

export async function deployToLocalWP(projectId: string): Promise<ApiResponse<LocalDeployResult>> {
  return request(`/projects/${projectId}/deploy-local`, { method: 'POST' });
}

export async function getLocalWPStatus(projectId: string): Promise<ApiResponse<{ running: boolean; url: string | null }>> {
  return request(`/projects/${projectId}/local-wp-status`);
}

export async function recheckDns(projectId: string): Promise<ApiResponse<{ configured: boolean; message: string; aRecord?: string; nameservers?: string[]; reason?: string }>> {
  return request(`/projects/${projectId}/dns-recheck`, { method: 'POST' });
}

// #859 (5.06) — optional async style pass after a cross-container
// deterministic move. Server-side env-gated (MOVE_STYLE_PASS); may only
// rewrite the moved element's style attribute, never content.
export async function moveStylePass(
  projectId: string,
  slug: string,
  movedHtml: string,
  movedDesc?: string,
  locator?: { tag: string; cls: string; inner: string },
): Promise<ApiResponse<{ applied: boolean; reason?: string }>> {
  return request(`/projects/${projectId}/pages/${slug}/move-style-pass`, {
    method: 'POST',
    body: JSON.stringify({ movedHtml, movedDesc, locator }),
  });
}

// #908/#910 — north-star move engine. The move tool sends the EXACT source +
// insertion reference (before|after|into) it computed from the drop point; the
// server applies a deterministic, content-preserving DOM move and returns the
// updated page HTML, or a single sensible clarify question when it can't be
// resolved (never a canned chooser).
export interface MovePagePayload {
  sourcePath: string;
  refPath: string;
  position: 'before' | 'after' | 'into';
  sourceHtml?: string;
  refHtml?: string;
  sourceDesc?: string;
  refDesc?: string;
}
export async function movePage(
  projectId: string,
  slug: string,
  payload: MovePagePayload,
): Promise<{ success: boolean; html?: string; clarify?: boolean; question?: string; reason?: string; error?: string }> {
  return request<any>(`/projects/${projectId}/pages/${slug}/move`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as any;
}

// #EDIT-MENUCFG — deterministic "set destination" popup confirm. The popup
// already knows exactly which element and which href it wants (no free text
// to interpret), so this calls a dedicated endpoint that runs applySetHref
// server-side — same shape as movePage/applyExactMove above, never the AI
// surgical editor.
export interface SetLinkHrefPayload {
  selectorPath?: string;
  newHref: string;
  linkTextHint?: string;
  currentHrefHint?: string;
}
export async function setLinkHref(
  projectId: string,
  slug: string,
  payload: SetLinkHrefPayload,
): Promise<{ success: boolean; html?: string; error?: string; reason?: string }> {
  return request<any>(`/projects/${projectId}/pages/${slug}/set-href`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }) as any;
}

// F3 (2026-05-17) — increment cumulative editor time for a page. Called
// every 30s from FullScreenEditor while the page is being actively edited
// (document.visibilityState === 'visible' + window has focus). secondsToAdd
// is clamped to [1, 90] on the backend.
export async function recordEditTime(
  projectId: string,
  slug: string,
  secondsToAdd: number,
): Promise<ApiResponse<{ totalEditSeconds: number }>> {
  return request(`/projects/${projectId}/pages/${slug}/edit-time`, {
    method: 'POST',
    body: JSON.stringify({ secondsToAdd }),
  });
}

// F3 (2026-05-17) — funnel analytics for the "Ready to go live?" modal.
// Feature 8 (2026-05-22) extends the event union with help_* events for
// the idle-help modal — same table, unified funnel view.
// Fire-and-forget — never block UX on the response.
export async function recordGoLivePromptEvent(
  projectId: string,
  event: 'shown' | 'dismissed' | 'clicked_publish' | 'clicked_setup99' | 'help_shown' | 'help_dismissed' | 'help_clicked',
  editSecondsAtEvent?: number,
): Promise<ApiResponse<unknown>> {
  return request(`/projects/${projectId}/golive-prompt-event`, {
    method: 'POST',
    body: JSON.stringify({ event, editSecondsAtEvent }),
  });
}

// #1384 — "Report bad generation" on an assistant chat reply. Sends the message id, an optional note,
// and the prompt trace the client retained from that turn's SSE done event (the entire prompt log).
export async function reportBadGeneration(
  projectId: string,
  messageId: string,
  reason?: string,
  trace?: unknown,
): Promise<ApiResponse<unknown>> {
  return request(`/projects/${projectId}/report-bad-generation`, {
    method: 'POST',
    body: JSON.stringify({ messageId, reason, trace }),
  });
}

// Feature 5 (2026-05-22) — intent-aware chat routing. Returns either
// { type: 'edit' } so the caller dispatches the existing /generate SSE
// flow, or { type: 'question', answer, citedSlugs, ... } when the message
// is a knowledgebase lookup. Persists both messages server-side on the
// question path.
export interface ChatRouteResult {
  type: 'edit' | 'question';
  answer?: string;
  citedSlugs?: string[];
  userMessageId?: string;
  assistantMessageId?: string;
}

export async function routeChatMessage(
  projectId: string,
  slug: string,
  message: string,
  currentRoute?: string,
  selectedHtml?: string,
  selectedSelector?: string,
): Promise<ApiResponse<ChatRouteResult>> {
  return request(`/projects/${projectId}/chat/route`, {
    method: 'POST',
    body: JSON.stringify({ message, slug, currentRoute, selectedHtml, selectedSelector }),
  });
}

// Feature 5 (follow-up) — project-less help answer for the HelpChatDrawer
// when no project context is available (Dashboard / Partner pages, etc.).
export async function answerHelpQuestion(
  message: string,
  currentRoute?: string,
): Promise<ApiResponse<ChatRouteResult>> {
  return request(`/projects/help-answer`, {
    method: 'POST',
    body: JSON.stringify({ message, currentRoute }),
  });
}

// Feature 6 (2026-05-22) — multi-source redesign materials.
export type SourceMaterialIntent = 'inspire_layout' | 'inspire_design' | 'content_source';
export type SourceMaterialKind = 'url' | 'pdf' | 'docx';
export interface SourceMaterial {
  id: string;
  kind: SourceMaterialKind;
  intent: SourceMaterialIntent;
  sourceValue: string;
  originalName?: string | null;
  sizeBytes?: number | null;
  mimeType?: string | null;
  injectionFlags?: string[];
  /** Task #599 — 'processing' while a URL source is extracted in the
   *  background; 'ready' once extracted_content/screenshot are filled in.
   *  Documents are extracted synchronously and come back 'ready'. */
  status?: 'processing' | 'ready' | 'failed';
  createdAt: string;
}

export async function listSourceMaterials(projectId: string): Promise<ApiResponse<SourceMaterial[]>> {
  return request(`/projects/${projectId}/source-materials`);
}

export async function addSourceUrl(
  projectId: string,
  url: string,
  intent: SourceMaterialIntent,
): Promise<ApiResponse<SourceMaterial>> {
  return request(`/projects/${projectId}/source-materials`, {
    method: 'POST',
    body: JSON.stringify({ kind: 'url', url, intent }),
  });
}

// Bug 25.05 #2: turn a failed multipart upload response into a clear,
// human message. nginx rejects oversized bodies with an HTML 413 (no JSON),
// while the backend's own caps return JSON 413/400 — handle both. Reading
// the body as text first lets us recover a backend `error` field when present
// without throwing on nginx's HTML page.
async function uploadErrorMessage(resp: Response): Promise<string> {
  if (resp.status === 413) {
    return 'That file is too large to upload. Please use a PDF or Word document under 25MB.';
  }
  const fromServer = await serverErrorMessage(resp, '');
  if (fromServer) return fromServer;
  if (resp.status === 401 || resp.status === 403) return 'Your session expired. Please sign in again and retry.';
  if (resp.status >= 500) return 'The server had trouble with that file. Please try again in a moment.';
  return `Upload failed (${resp.status}). Please try again with a smaller file.`;
}

// #2289 — re-exported so raw-Response callers (the tax-form downloads) show the server's friendly error.
export { serverErrorMessage };

export async function addSourceDocument(
  projectId: string,
  file: File,
  intent: SourceMaterialIntent,
): Promise<ApiResponse<SourceMaterial>> {
  const kind: SourceMaterialKind = file.name.toLowerCase().endsWith('.pdf') ? 'pdf' : 'docx';
  const fd = new FormData();
  fd.append('file', file);
  fd.append('kind', kind);
  fd.append('intent', intent);
  // #1390 — authedFetch (401 -> single-flight refresh -> retry -> else session-expired) not bare fetch.
  const resp = await authedFetch(`/projects/${projectId}/source-materials`, {
    method: 'POST',
    body: fd,
  });
  // Bug 25.05 #2: a too-large file is rejected by nginx (HTML 413) or by
  // multer (JSON 413) — either way resp.json() on an HTML body throws and
  // the old code fell back to an opaque "Upload failed". Map the status to
  // a clear, actionable message before attempting to parse the body.
  if (!resp.ok) {
    return { success: false, error: await uploadErrorMessage(resp) } as ApiResponse<SourceMaterial>;
  }
  const json = await resp.json().catch(() => ({ success: false, error: 'Upload failed' }));
  return json as ApiResponse<SourceMaterial>;
}

export async function deleteSourceMaterial(
  projectId: string,
  materialId: string,
): Promise<ApiResponse<unknown>> {
  return request(`/projects/${projectId}/source-materials/${materialId}`, {
    method: 'DELETE',
  });
}

export async function updateSourceMaterialIntent(
  projectId: string,
  materialId: string,
  intent: SourceMaterialIntent,
): Promise<ApiResponse<unknown>> {
  return request(`/projects/${projectId}/source-materials/${materialId}`, {
    method: 'PATCH',
    body: JSON.stringify({ intent }),
  });
}

// Feature 3 (2026-05-22) — paperclip chat attachments (PDF / .docx).
export interface ChatAttachment {
  id: string;
  kind: 'pdf' | 'docx' | 'image';
  originalName: string;
  sizeBytes: number;
  pageCount?: number;
  hasText: boolean;
  injectionFlags: string[];
  createdAt: string;
}

export async function uploadChatAttachment(
  projectId: string,
  file: File,
): Promise<ApiResponse<ChatAttachment>> {
  const fd = new FormData();
  fd.append('file', file);
  // #1390 — authedFetch (401 -> single-flight refresh -> retry -> else session-expired) not bare fetch.
  const resp = await authedFetch(`/projects/${projectId}/chat-attachments`, {
    method: 'POST',
    body: fd,
  });
  if (!resp.ok) {
    return { success: false, error: await uploadErrorMessage(resp) } as ApiResponse<ChatAttachment>;
  }
  const json = await resp.json().catch(() => ({ success: false, error: 'Upload failed' }));
  return json as ApiResponse<ChatAttachment>;
}

export async function deleteChatAttachment(
  projectId: string,
  attId: string,
): Promise<ApiResponse<unknown>> {
  return request(`/projects/${projectId}/chat-attachments/${attId}`, {
    method: 'DELETE',
  });
}

// F5 (2026-05-17) — admin VPS analytics endpoints
export interface VpsLatestRow {
  host: string;
  cpu_load_1m: number | null;
  cpu_load_5m: number | null;
  cpu_load_15m: number | null;
  mem_used_mb: number | null;
  mem_total_mb: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
  uptime_seconds: number | null;
  recorded_at: string;
}
export interface VpsHistoryRow {
  host: string;
  cpu_load_1m: number | null;
  mem_used_mb: number | null;
  mem_total_mb: number | null;
  disk_used_gb: number | null;
  disk_total_gb: number | null;
  recorded_at: string;
}
export async function getAdminVpsLatest(): Promise<ApiResponse<VpsLatestRow[]>> {
  return request('/admin/monitoring/vps-system/latest');
}
export async function getAdminVpsHistory(hours = 24, host?: string): Promise<ApiResponse<{ hours: number; host: string | null; samples: VpsHistoryRow[] }>> {
  const qs = new URLSearchParams({ hours: String(hours) });
  if (host) qs.set('host', host);
  return request(`/admin/monitoring/vps-system/history?${qs.toString()}`);
}
export async function getAdminVpsRecentRecovery(limit = 50): Promise<ApiResponse<{ events: Array<{ id: string; project_id: string; action: string; result: string; source: string | null; notes: string | null; created_at: string; domain: string | null }>; count: number }>> {
  return request(`/admin/monitoring/vps-system/recent-recovery?limit=${limit}`);
}
export interface AdminDeployedClient {
  projectId: string;
  domain: string | null;
  deployUrl: string | null;
  status: string | null;
  wpSiteUrl: string | null;
  clientId: string;
  businessName: string | null;
  clientTier: string | null;
  customerEmail: string | null;
  projectUpdatedAt: string | null;
}
export async function getAdminMonitoringDeployedClients(): Promise<ApiResponse<{ clients: AdminDeployedClient[]; count: number }>> {
  return request('/admin/monitoring/deployed-clients');
}

// RESREP-006 — admin-only per-ticket read for AdminTicketsPage's "Auto-repair
// attempted" card. Reuses the GET /resolution-reports aggregate endpoint with
// a ticketId filter rather than a new route (see admin-monitoring.routes.ts).
export interface TicketResolutionReport {
  reportId: string;
  ticketId: string;
  projectId: string | null;
  idempotencyKey: string;
  resolvedAt: string;
  resolvedBy: { type: string; [key: string]: unknown };
  outcome: string;
  rootCause: { category: string; detail?: string; confidence?: number };
  filesModified: Array<{ path: string; action: string }>;
  actionsExecuted: string[];
  pipelineFeedback: Record<string, unknown>;
  customerSummary: string | null;
  metrics: Record<string, unknown>;
  customerReplyPosted: boolean;
  createdAt: string;
}
export async function getTicketResolutionReports(ticketId: string): Promise<ApiResponse<{ reports: TicketResolutionReport[]; count: number; nextCursor: string | null }>> {
  return request(`/admin/monitoring/resolution-reports?ticketId=${encodeURIComponent(ticketId)}`);
}

// F1 (2026-05-17) — Bedrock spend controls
export interface BedrockSpendSnapshot {
  monthlyLimitCents: number;
  totalLimitCents: number;
  monthlyWindowStart: string;
  totalWindowStart: string;
  isDisabled: boolean;
  disabledReason: string | null;
  updatedAt: string;
  updatedBy: string | null;
  monthlySpentCents: number;
  totalSpentCents: number;
  monthlyPct: number;
  totalPct: number;
  monthlyKey: string;
}
export interface BedrockAuditEntry {
  id: string;
  action: string;
  admin_user_id: string | null;
  previous_value: unknown;
  new_value: unknown;
  reason: string | null;
  created_at: string;
}
export async function getAdminBedrockSpend(): Promise<ApiResponse<{ snapshot: BedrockSpendSnapshot; audit: BedrockAuditEntry[] }>> {
  return request('/admin/bedrock-spend');
}
export async function updateAdminBedrockSpendLimits(monthlyLimitDollars?: number, totalLimitDollars?: number, reason?: string): Promise<ApiResponse<BedrockSpendSnapshot>> {
  return request('/admin/bedrock-spend', { method: 'PUT', body: JSON.stringify({ monthlyLimitDollars, totalLimitDollars, reason }) });
}
export async function resetAdminBedrockTotal(reason?: string): Promise<ApiResponse<BedrockSpendSnapshot>> {
  return request('/admin/bedrock-spend/reset-total', { method: 'POST', body: JSON.stringify({ reason }) });
}
export async function toggleAdminBedrockDisabled(disabled: boolean, reason?: string): Promise<ApiResponse<BedrockSpendSnapshot>> {
  return request('/admin/bedrock-spend/toggle-disabled', { method: 'POST', body: JSON.stringify({ disabled, reason }) });
}
export async function runAdminBedrockSmokeTest(): Promise<ApiResponse<{ text: string; model: string; inputTokens: number; outputTokens: number; costUsd: number; snapshotAfter: BedrockSpendSnapshot }>> {
  return request('/admin/bedrock-spend/smoke-test', { method: 'POST' });
}

export async function generatePageHtml(
  projectId: string,
  slug: string,
  prompt: string,
  onToken?: (text: string) => void,
  sourceUrl?: string,
  onStatus?: (message: string) => void,
  regenerate?: boolean,
  onEstimate?: (seconds: number) => void,
  sourceUrls?: string[],
  signal?: AbortSignal,
  overrideColors?: ColorScheme,
  // `displayPrompt` is the user-facing chat content stored on the chat
  // user-row; falls back to `prompt` when not supplied. Used by the
  // tool-prompt humanizer to keep technical scaffolding out of the panel.
  displayPrompt?: string,
  // `onRunId` is invoked when the backend emits the generation_run_id SSE
  // event — the caller uses this id to call notifyOnComplete(...) when the
  // user asks "email me when this is ready" or backgrounds the tab.
  onRunId?: (runId: string) => void,
  // PROJCHAT-011 — viewport mode at the moment the user submitted the edit,
  // captured into project_chat_messages.device_mode for later analysis.
  deviceMode?: 'mobile' | 'tablet' | 'desktop',
  // Feature 3 (2026-05-22) — paperclip attachment IDs. Backend loads
  // each, scrubs+envelopes the extracted text, appends to fullPrompt.
  attachmentIds?: string[],
  // SF_PLANNER_THINKING — raw token-by-token planner reasoning (the 'thinking'
  // SSE event), rendered as a live reasoning lane in the editor chat. Undefined
  // (or flag off) = today's sanitized status only.
  onThinking?: (delta: string) => void,
  // #1222 (VITE_SF_VARIATION_SCOPE) — the HTML of the CURRENTLY-DISPLAYED design
  // variation. The FE holds multiple variations per slug (pageVersions[slug]);
  // an edit must apply to the variation the user is looking at, not the
  // canonical sites.pages[slug]. The backend honors this as the edit base when
  // present. Omitted (or flag off) = backend falls back to the canonical page.
  baseHtml?: string,
  // #1241 (VITE_SF_VARIATION_SCOPE) — id of the CURRENTLY-ACTIVE design variation
  // (guided flow only). Sent so the backend records the edit + its chat row
  // against the right per-variation thread. ManagePage passes 'canonical' (its
  // single page). Omitted (or flag off) = today's behavior (no variant scoping).
  variantId?: string,
): Promise<ApiResponse<GeneratePageResult>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const body: Record<string, unknown> = { prompt };
  const hasSource = (sourceUrls && sourceUrls.length > 0) || !!sourceUrl;
  if (sourceUrls && sourceUrls.length > 0) {
    body.sourceUrls = sourceUrls;
  } else if (sourceUrl) {
    body.sourceUrl = sourceUrl;
  }
  if (regenerate) body.regenerate = true;
  if (overrideColors) body.colors = overrideColors;
  if (displayPrompt && displayPrompt !== prompt) body.displayPrompt = displayPrompt;
  if (deviceMode) body.deviceMode = deviceMode;
  if (attachmentIds && attachmentIds.length > 0) body.attachmentIds = attachmentIds;
  // #1222 — send the active variation's HTML so the backend edits the variation
  // the user is viewing, not the canonical sites.pages[slug]. Default-on; the
  // VITE_SF_VARIATION_SCOPE=off kill-switch restores canonical-page editing.
  if (baseHtml && import.meta.env.VITE_SF_VARIATION_SCOPE !== 'off') body.baseHtml = baseHtml;
  // #1241 — scope this edit (and its chat row) to the active design variation.
  if (variantId && import.meta.env.VITE_SF_VARIATION_SCOPE !== 'off') body.variantId = variantId;
  // One-shot resize rect (#1069), set by the editor right before this call when
  // the user asked to resize a selected element. Consumed (removed) once read so
  // it can't leak into a subsequent unrelated edit.
  try {
    const rectRaw = sessionStorage.getItem('sf_selection_rect');
    if (rectRaw) { body.selectionRect = JSON.parse(rectRaw); sessionStorage.removeItem('sf_selection_rect'); }
  } catch { /* ignore */ }
  // One-shot DALL-E permission flag, set by the editor's asset-permission
  // action UI right before this call. Consumed (removed) once read so it
  // doesn't leak into subsequent unrelated edits.
  try {
    if (sessionStorage.getItem('sf_permit_dalle_next') === '1') {
      body.permitDalle = true;
      sessionStorage.removeItem('sf_permit_dalle_next');
    }
    // Signals the orchestrator's planner that the user has already answered
    // the asset-permission clarify for THIS request — 'generate' (clicked
    // Generate with AI), 'upload' (clicked Upload your own and uploads
    // succeeded), or 'skip-missing' (clicked Skip missing on a partial-
    // success popup). The planner uses this to suppress its possessive-
    // asset clarify directive so it doesn't ask the same question on the
    // resubmit. Consumed once.
    const ca = sessionStorage.getItem('sf_clarify_answered_next');
    // #810b — 'pick-suggestion' marks a generic clarify-chip answer (e.g. the
    // scope-mismatch "which did you mean?" chips) so the route skips its
    // pre-flight detectors on the resubmit instead of re-asking the same
    // question. 'embed-url-provided' was set by the editor but missing from
    // this whitelist (same re-ask bug class) — included now.
    // INV-11 — 'reference-selected' marks a compare/align resubmit where the
    // user clicked the REFERENCE element (its selector is baked into the prompt
    // as a "REFERENCE ELEMENT" block). Like the others it must be forwarded so
    // the route skips its pre-flight detectors (incl. the compare-gate that
    // asked the question) and never re-asks. Omitting it here silently dropped
    // the signal AND leaked the sessionStorage key into the next edit.
    if (ca === 'generate' || ca === 'upload' || ca === 'skip-missing' || ca === 'multi-page-this-only'
        || ca === 'multi-page-all'
        || ca === 'embed-url-provided' || ca === 'pick-suggestion' || ca === 'user-clarified'
        || ca === 'reference-selected' || ca === 'force-apply') {
      body.clarifyAnswered = ca;
      sessionStorage.removeItem('sf_clarify_answered_next');
    }
    // #830 — free-text clarify answer, set by the vague-ask panel. Only
    // forwarded when clarifyAnswered is 'user-clarified'. Consumed once.
    if (ca === 'user-clarified') {
      const ct = sessionStorage.getItem('sf_clarify_text_next');
      if (ct) { body.clarifyText = ct; sessionStorage.removeItem('sf_clarify_text_next'); }
    }
    // GAPS P2 / 12.06 Issue #1 — structured force-apply resume: the exact
    // {selector, property, value} the failed edit intended, applied
    // deterministically by the route (no planner round-trip). Consumed once.
    if (ca === 'force-apply') {
      const fa = sessionStorage.getItem('sf_force_apply_next');
      if (fa) {
        try {
          const parsed = JSON.parse(fa);
          if (parsed && typeof parsed.selector === 'string' && typeof parsed.property === 'string' && typeof parsed.value === 'string') {
            body.forceApply = parsed;
          }
        } catch { /* malformed — ignore */ }
        sessionStorage.removeItem('sf_force_apply_next');
      }
    }
    // #873 (5.06-B) — clarify-round counter. The popup handlers write the
    // CURRENT question's round number (from attachmentMeta.clarifyRound) so
    // the route can count rounds per intent and hard-stop at 3: the cap
    // refuses + reports instead of asking a 4th time (and never falls
    // through to best-effort execution). Consumed once.
    const cr = sessionStorage.getItem('sf_clarify_round_next');
    if (cr) {
      const n = parseInt(cr, 10);
      if (Number.isFinite(n) && n > 0) body.clarifyRound = Math.min(n, 10);
      sessionStorage.removeItem('sf_clarify_round_next');
    }
    // For skip-missing specifically, the popup also forwards the list of
    // brand names the surgical editor should keep as styled text rather
    // than try to find images for. Carried in a separate sessionStorage
    // key so the api layer doesn't have to know about brand metadata.
    if (ca === 'skip-missing') {
      const mb = sessionStorage.getItem('sf_missing_brands_next');
      if (mb) {
        try {
          const parsed = JSON.parse(mb);
          if (Array.isArray(parsed)) body.missingBrandsToKeepAsText = parsed.filter((s) => typeof s === 'string');
        } catch { /* malformed — ignore */ }
        sessionStorage.removeItem('sf_missing_brands_next');
      }
    }
    // Phase 3.2 — external-embed URL hand-off. The popup writes embedType
    // + embedUrl to sessionStorage on Apply; api layer forwards them to
    // the route so the EXTERNAL-EMBED DIRECTIVE can be appended.
    if (ca === 'embed-url-provided') {
      const et = sessionStorage.getItem('sf_embed_type_next');
      const eu = sessionStorage.getItem('sf_embed_url_next');
      if (et) { body.embedType = et; sessionStorage.removeItem('sf_embed_type_next'); }
      if (eu) { body.embedUrl = eu; sessionStorage.removeItem('sf_embed_url_next'); }
    }
    // 2026-05-28 Bree fix — authoritative page-extracted brand list from the
    // ASSET_NEEDED / partial popup. Drives the logo fetch (searchAndPersistAssets
    // explicitBrands) so it searches the real retailers, not the planner's
    // style-guess query. Set on Generate-with-AI + Skip-missing.
    const ab = sessionStorage.getItem('sf_asset_brands_next');
    if (ab) {
      try {
        const parsed = JSON.parse(ab);
        if (Array.isArray(parsed)) body.assetBrandsForFetch = parsed.filter((s) => typeof s === 'string');
      } catch { /* malformed — ignore */ }
      sessionStorage.removeItem('sf_asset_brands_next');
    }
  } catch { /* sessionStorage unavailable — ignore */ }

  // GTM sf_design_started — track every generation kickoff. useCase is
  // redesign when a source URL is provided, new_site otherwise.
  const { trackDesignStarted, trackDesignCompleted } = await import('./analytics');
  const useCase: 'redesign' | 'new_site' = hasSource ? 'redesign' : 'new_site';
  const mode: 'generate' | 'redesign' | 'edit' = regenerate ? 'edit' : (hasSource ? 'redesign' : 'generate');
  const generationStartedAt = Date.now();
  trackDesignStarted({
    projectId,
    useCase,
    sourceUrl: sourceUrl || (sourceUrls && sourceUrls[0]),
    mode,
  });

  // #8.06 bug 1 — a connection dropped at request time makes fetch() REJECT
  // with TypeError "Failed to fetch" (distinct from the in-stream idle stall
  // the 6.06 fix already handles). Translate that reject into the same friendly
  // CONNECTION_LOST result so callers show a clear message instead of the raw
  // browser error. A user abort rejects with AbortError — re-throw it so the
  // cancel path still works.
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/projects/${projectId}/pages/${slug}/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    });
  } catch (e: any) {
    if (signal?.aborted || e?.name === 'AbortError') throw e;
    return {
      success: false,
      code: 'CONNECTION_LOST',
      error: "Your connection dropped, so the change couldn't finish. Check your internet connection and try again. Your page hasn't been changed.",
    };
  }

  // The 15m access token can expire exactly as this edit is sent (before the
  // background poller refreshes it). Refresh once (single-flight, shared with
  // the rest of api.ts) and retry the POST so the edit doesn't silently fail on
  // a 401 — the stream hasn't been consumed yet, so re-POSTing is safe.
  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      headers['Authorization'] = `Bearer ${newAccess}`;
      try {
        res = await fetch(`${API_BASE}/projects/${projectId}/pages/${slug}/generate`, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
          signal,
        });
      } catch (e: any) {
        if (signal?.aborted || e?.name === 'AbortError') throw e;
        return {
          success: false,
          code: 'CONNECTION_LOST',
          error: "Your connection dropped, so the change couldn't finish. Check your internet connection and try again. Your page hasn't been changed.",
        };
      }
    } else {
      // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
      // gated — see handleSessionExpired); this request still fails either way.
      return { success: false, error: "Your session expired. Please sign in again and retry — your page hasn't been changed." };
    }
  }

  if (!res.ok) {
    // Forward the backend's machine-readable `code` (if any) alongside the
    // human error string so callers can switch on it — e.g. AddPageModal
    // routes to UpgradeModal on `code: 'PAGE_LIMIT_REACHED'` instead of
    // showing the raw error banner.
    try { const err = await res.json(); return { success: false, error: err.error || `Server error: ${res.status}`, code: err.code }; }
    catch { return { success: false, error: `Server error: ${res.status}` }; }
  }

  // D3 — a 200 that ISN'T an SSE stream (e.g. the backend short-circuits with a JSON
  // body before switching to text/event-stream) would make the reader below find no
  // SSE lines and surface a bogus "Stream ended unexpectedly", swallowing the real
  // message. Same guard analyzeUrl / getConsolidationRecommendations already use.
  if (!(res.headers.get('content-type') || '').includes('text/event-stream')) {
    try {
      const body = await res.json();
      return { success: false, error: (body && (body.error || body.message)) || 'Generation could not start. Please try again in a moment.', code: body?.code };
    } catch {
      return { success: false, error: 'Generation could not start. Please try again in a moment.' };
    }
  }

  if (!res.body) return { success: false, error: 'No response body' };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: ApiResponse<GeneratePageResult> = { success: false, error: 'Stream ended unexpectedly' };
  let eventType = '';

  const processLines = (lines: string[]) => {
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (eventType === 'status' && onStatus) {
          try { const parsed = JSON.parse(data); onStatus(parsed.message); } catch { /* skip */ }
        } else if (eventType === 'thinking' && onThinking) {
          // SF_PLANNER_THINKING — raw planner reasoning delta (token-by-token).
          try { const parsed = JSON.parse(data); if (parsed?.delta) onThinking(parsed.delta); } catch { /* skip */ }
        } else if (eventType === 'estimate' && onEstimate) {
          try { const parsed = JSON.parse(data); onEstimate(parsed.seconds); } catch { /* skip */ }
        } else if (eventType === 'generation_run_id' && onRunId) {
          try { const parsed = JSON.parse(data); if (parsed?.id) onRunId(parsed.id); } catch { /* skip */ }
        } else if (eventType === 'token' && onToken) {
          try { onToken(JSON.parse(data)); } catch { /* skip */ }
        } else if (eventType === 'deduplicated') {
          // Bug 12.05/1: the backend dedup branch ends the SSE early
          // (no `done` event) when another tab is already generating
          // this slug. Previously this surfaced to the wizard as
          // "Stream ended unexpectedly" → red error → poll later swapped
          // to spinner. Mark the response as a benign deduped success so
          // the caller doesn't render an error; the wizard's 5-second
          // in-flight poll then attaches to the existing run's progress.
          try {
            const parsed = JSON.parse(data) as { runId?: string };
            result = { success: true, data: { html: '', deduplicated: true, existingRunId: parsed.runId } as any };
          } catch {
            result = { success: true, data: { html: '', deduplicated: true } as any };
          }
        } else if (eventType === 'done') {
          try {
            result = { success: true, data: JSON.parse(data) };
            trackDesignCompleted({
              projectId,
              useCase,
              mode,
              generationSeconds: (Date.now() - generationStartedAt) / 1000,
            });
          } catch { result = { success: false, error: 'Failed to parse response' }; }
        } else if (eventType === 'question') {
          // Phase 3 (3C): the agent paused to ask a clarifying question instead
          // of editing. NOT an error — the question is already persisted as an
          // assistant chat row server-side. Surface it as a benign result with
          // empty html + a `question` marker so the caller skips the html apply
          // and the chat re-fetch surfaces the question to the user.
          try { const q = JSON.parse(data); result = { success: true, data: { html: '', question: q.question, runId: q.runId } as any }; }
          catch { result = { success: true, data: { html: '' } as any }; }
        } else if (eventType === 'error') {
          // Same code-passthrough fix as the HTTP-level error path above.
          try { const errData = JSON.parse(data); result = { success: false, error: errData.error || 'Unknown error', code: errData.code }; } catch { result = { success: false, error: data }; }
        }
        eventType = '';
      }
    }
  };

  // Bug #4 (6.06): detect a dropped/dead connection mid-generation. The backend
  // SSE writes a `: keepalive` comment every 30s (project.routes.ts ~:4669), so a
  // healthy stream always delivers bytes <=30s apart, even during long silent LLM
  // phases. If NOTHING arrives for 90s (3x the heartbeat) the connection is gone —
  // without this guard reader.read() awaits forever and the editor spins with no
  // notification (the user thinks it is still working). 90s stays well under the
  // ">5 min appears-to-run-forever" the bug describes while never false-firing on
  // a healthy stream. A user-initiated abort still propagates as AbortError.
  const CHUNK_IDLE_TIMEOUT_MS = 90_000;
  while (true) {
    let idleTimer: ReturnType<typeof setTimeout> | undefined;
    const chunk = await Promise.race([
      reader.read(),
      new Promise<never>((_, reject) => {
        idleTimer = setTimeout(() => reject(new Error('SF_CONNECTION_IDLE')), CHUNK_IDLE_TIMEOUT_MS);
      }),
    ]).catch((e: any) => {
      if (signal?.aborted || e?.name === 'AbortError') throw e; // user cancel — let the caller handle it
      return 'SF_CONNECTION_LOST' as const;
    }).finally(() => { if (idleTimer) clearTimeout(idleTimer); });
    if (chunk === 'SF_CONNECTION_LOST') {
      try { await reader.cancel(); } catch { /* already closed */ }
      // GP-2: 90s of byte-silence can mean the user's network dropped OR the
      // generation service stalled server-side — the client genuinely can't
      // tell, so don't assert it's the user's internet (the reporter was
      // connected fine). VITE_SF_HONEST_CONN_ERR=off restores the old wording.
      const honestConnErr = ((import.meta.env as Record<string, string | undefined>).VITE_SF_HONEST_CONN_ERR ?? 'on') !== 'off';
      return {
        success: false,
        code: 'CONNECTION_LOST',
        error: honestConnErr
          ? "The connection to the generation service was lost before it finished — this can be a temporary server-side issue or a network drop. Please try again in a moment. Your page hasn't been changed."
          : "Your connection dropped, so the change couldn't finish. Check your internet connection and try again. Your page hasn't been changed.",
      };
    }
    const { done, value } = chunk;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    processLines(lines);
  }
  // Process any remaining buffer content after stream ends
  if (buffer.trim()) {
    processLines(buffer.split('\n'));
  }
  // If stream ended without done/error, try fetching the latest page state —
  // the backend may have finished writing after the connection dropped.
  // This recovers silently from flaky proxies/CDNs and avoids scaring the user.
  if (!result.success && result.error === 'Stream ended unexpectedly') {
    try {
      const latest = await getPageHtml(projectId, slug);
      if (latest.success && latest.data?.html) {
        result = {
          success: true,
          data: {
            slug,
            title: '',
            htmlPath: '',
            html: latest.data.html,
            assistantMessage: 'Saved.',
          },
        };
        trackDesignCompleted({
          projectId,
          useCase,
          mode,
          generationSeconds: (Date.now() - generationStartedAt) / 1000,
        });
      } else {
        result.error = 'The generation is still running in the background. Please wait a moment and refresh if your changes don\'t appear.';
      }
    } catch {
      result.error = 'The generation is still running in the background. Please wait a moment and refresh if your changes don\'t appear.';
    }
  }
  return result;
}

/**
 * Opt the in-flight generation run in for a completion email so the user
 * can leave the tab during a long-running generation. Optional `email`
 * arg lets the user pick a different inbox; otherwise the backend uses
 * their account email. No-ops with `ok: false` if the run already
 * finished by the time the request lands.
 */
export async function notifyOnComplete(
  projectId: string,
  runId: string,
  email?: string,
): Promise<ApiResponse<{ ok: boolean; reason?: string; email?: string }>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  try {
    const res = await fetch(`${API_BASE}/projects/${projectId}/generation-runs/${runId}/notify`, {
      method: 'POST',
      headers,
      body: JSON.stringify(email ? { email } : {}),
    });
    const json = (await res.json()) as ApiResponse<{ ok: boolean; reason?: string; email?: string }>;
    return json;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error' };
  }
}

/**
 * #BUG-B (8.08 QA report) — explicit cancel signal for an in-flight edit's
 * generation_runs row. Aborting the fetch (AbortController) alone only stops
 * THIS tab from waiting — the backend deliberately keeps a bare disconnect's
 * run 'running' server-side (so a flaky network drop can still recover the
 * edit ~1-2min later; see the #43/#981 notes on the /generate close-listener),
 * and it can't tell a passive drop from a deliberate Cancel click. Left alone,
 * that meant the very next edit on the page was rejected with a false
 * EDIT_BUSY for up to that same window. Call this alongside the abort() so
 * the backend frees the slot immediately instead of waiting on the lenient
 * disconnect path or the 15-minute sweeper. Fire-and-forget from the caller —
 * failure here is non-fatal, the existing recovery/sweeper paths still apply.
 */
export async function cancelGenerationRun(
  projectId: string,
  runId: string,
): Promise<ApiResponse<{ ok: boolean; reason?: string; status?: string }>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  try {
    const res = await fetch(`${API_BASE}/projects/${projectId}/generation-runs/${runId}/cancel`, {
      method: 'POST',
      headers,
    });
    const json = (await res.json()) as ApiResponse<{ ok: boolean; reason?: string; status?: string }>;
    return json;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error' };
  }
}

/**
 * #BUG-B race fix — cancel the active EDIT run for a page when the caller does
 * NOT yet have a runId (Cancel clicked before the `generation_run_id` SSE frame
 * arrived, so editRunId is still null and cancelGenerationRun can't be used).
 * The server resolves the active edit-class run for the page. Fire-and-forget,
 * same as cancelGenerationRun — failure falls back to the recovery/sweeper paths.
 */
export async function cancelActiveEditForPage(
  projectId: string,
  slug: string,
): Promise<ApiResponse<{ ok: boolean; reason?: string }>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  try {
    const res = await fetch(`${API_BASE}/projects/${projectId}/pages/${encodeURIComponent(slug)}/cancel-active-edit`, {
      method: 'POST',
      headers,
    });
    return (await res.json()) as ApiResponse<{ ok: boolean; reason?: string }>;
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error' };
  }
}

// ── Brand API ──

export interface BrandConfigData {
  id: string;
  slug: string;
  companyName: string;
  tagline?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  footerNote?: string;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  isDefault: boolean;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
  // #2370: true only when this brand belongs to a reseller partner (never set for the platform's own brand pages).
  isReseller?: boolean;
}

export async function getBrands(): Promise<ApiResponse<BrandConfigData[]>> {
  return request('/brands');
}

export async function getBrand(slug: string): Promise<ApiResponse<BrandConfigData>> {
  return request(`/brands/${slug}`);
}

export async function createBrand(data: Partial<BrandConfigData>): Promise<ApiResponse<BrandConfigData>> {
  return request('/brands', { method: 'POST', body: JSON.stringify(data) });
}

export async function updateBrand(id: string, data: Partial<BrandConfigData>): Promise<ApiResponse<BrandConfigData>> {
  return request(`/brands/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

export async function uploadBrandLogo(brandId: string, file: File): Promise<ApiResponse<{ url: string }>> {
  const formData = new FormData();
  formData.append('image', file);

  const headers: Record<string, string> = {};
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const res = await fetch(`${API_BASE}/brands/${brandId}/upload-logo`, {
    method: 'POST',
    headers,
    body: formData,
  });

  return res.json();
}

// ── Outreach API ──

export interface OutreachPreviewData {
  id: string;
  token: string;
  sourceUrl?: string;
  prompt?: string;
  htmlS3Path?: string;
  status: 'pending' | 'generating' | 'ready' | 'expired';
  packageSlug?: string;
  views: number;
  createdBy?: string;
  convertedOrderId?: string;
  convertedAt?: string;
  error?: string;
  expiresAt?: string;
  createdAt: string;
  businessName?: string;
  email?: string;
  contactName?: string;
  socialUrl?: string;
  description?: string;
  webhookUrl?: string;
  styleUsed?: string;
  userId?: string;
  projectId?: string;
  discoveredPages?: Array<{ title: string; sourceUrl: string }>;
  htmlSignature?: string;
  htmlSignatureTimestamp?: number;
  bannerRegularPrice?: string;
  bannerSalePrice?: string;
  bannerDemoUrl?: string;
  // #1810 — replay variant provenance (origin='replay' + label) + the review-status mirror.
  origin?: 'fresh' | 'replay';
  replayLabel?: string | null;
  reviewStatus?: string | null;
}

export interface OutreachGenerateRequest {
  businessName: string;
  email: string;
  sourceUrl?: string;
  socialUrl?: string;
  description?: string;
  contactName?: string;
  webhookUrl?: string;
  prompt?: string;
  packageSlug?: string;
}

export async function generateOutreachPreview(data: OutreachGenerateRequest): Promise<ApiResponse<OutreachPreviewData>> {
  return request('/outreach/generate', { method: 'POST', body: JSON.stringify(data) });
}

export async function listOutreachPreviews(): Promise<ApiResponse<OutreachPreviewData[]>> {
  return request('/outreach');
}

export async function getOutreachPreview(token: string): Promise<ApiResponse<OutreachPreviewData>> {
  return request(`/outreach/${token}`);
}

export async function trackOutreachView(token: string): Promise<ApiResponse<void>> {
  return request(`/outreach/${token}/view`, { method: 'POST' });
}

export async function deleteOutreachPreview(id: string): Promise<ApiResponse<void>> {
  return request(`/outreach/${id}`, { method: 'DELETE' });
}

export async function trackOutreachEvent(token: string, eventType: string): Promise<ApiResponse<void>> {
  return request(`/outreach/${token}/event`, { method: 'POST', body: JSON.stringify({ eventType }) });
}

export async function getOutreachAdminStats(period?: string): Promise<ApiResponse<unknown>> {
  const qs = period ? `?period=${period}` : '';
  return request(`/outreach/admin/stats${qs}`);
}

export interface OutreachLogRow {
  id: string;
  logType: 'api_request' | 'generation' | 'webhook';
  direction: 'inbound' | 'outbound' | 'internal';
  status: 'pending' | 'success' | 'failed' | 'retrying' | null;
  httpStatus: number | null;
  httpMethod: string | null;
  url: string | null;
  requestBody: unknown;
  responseBody: unknown;
  errorMessage: string | null;
  attemptNumber: number;
  durationMs: number | null;
  createdAt: string;
}

export interface OutreachPreviewLogs {
  preview: {
    id: string;
    token: string;
    businessName?: string;
    email?: string;
    status: string;
    webhookUrl?: string | null;
    styleUsed?: string;
    createdAt: string;
    inputTokens?: number;
    outputTokens?: number;
    apiCostCents?: number;
  };
  logs: OutreachLogRow[];
}

export async function getOutreachPreviewLogs(previewId: string): Promise<ApiResponse<OutreachPreviewLogs>> {
  return request(`/outreach/admin/previews/${previewId}/logs`);
}

// ── Auto-review analysis (the #379 gen_reviews verdict for a preview, for the outreach UI) ──
export interface GenReviewFinding {
  section: string;
  class: string;
  severity: string | null;
  note: string;
  slug: string | null;
}
export interface GenReviewAnalysis {
  token: string;
  previewUrl: string;
  reviewStatus: string;            // NONE | PENDING | CLEAN | DEFECTS | FAILED
  defectSections: number | null;
  sectionsReviewed: number | null;
  reviewedAt: string | null;
  defects: GenReviewFinding[];
}
export async function getGenReviewAnalysis(token: string): Promise<ApiResponse<GenReviewAnalysis>> {
  return request(`/gen-review/${encodeURIComponent(token)}`);
}

// ── Help Request API ──

export async function getHelpBalance(): Promise<ApiResponse<{ totalHours: number; usedHours: number; remainingHours: number; pendingRequests: number }>> {
  return request('/help/balance');
}

export async function getHelpRequests(projectId?: string): Promise<ApiResponse<any[]>> {
  const params = projectId ? `?projectId=${projectId}` : '';
  return request(`/help/requests${params}`);
}

export async function submitHelpRequest(data: FormData): Promise<ApiResponse<any>> {
  // #1390 — authedFetch (401 -> single-flight refresh -> retry -> else session-expired) not bare fetch.
  // (Content-Type unset on purpose — the browser sets the multipart boundary.)
  const res = await authedFetch(`/help/requests`, {
    method: 'POST',
    body: data,
  });
  return res.json();
}

// ── Concierge API (Phase 3.1) — AI support concierge. Streaming,
// server-persisted chat for logged-in CLIENT users. Backend base
// /api/concierge; every conversation is scoped to the caller's JWT. ──

export interface ConciergeMessage {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  toolName: string | null;
  createdAt: string;
}

export interface ConciergeConversationSummary {
  id: string;
  title: string | null;
  updatedAt: string;
}

export async function createConciergeConversation(): Promise<ApiResponse<{ id: string }>> {
  return request('/concierge/conversations', { method: 'POST' });
}

export async function listConciergeConversations(): Promise<ApiResponse<ConciergeConversationSummary[]>> {
  return request('/concierge/conversations');
}

export async function getConciergeConversation(
  id: string,
): Promise<ApiResponse<{ id: string; title: string | null; messages: ConciergeMessage[] }>> {
  return request(`/concierge/conversations/${id}`);
}

export async function deleteConciergeConversation(id: string): Promise<ApiResponse<unknown>> {
  return request(`/concierge/conversations/${id}`, { method: 'DELETE' });
}

export interface ConciergeStreamHandlers {
  onToken?: (text: string) => void;
  onToolCall?: (name: string, input: any) => void;
  onToolResult?: (name: string, summary: string) => void;
  onDone?: (text: string) => void;
  onError?: (error: string) => void;
}

// SSE turn stream — mirrors generatePageHtml's getReader/TextDecoder loop shape
// (buffer accumulated across chunks, split on '\n', last partial line held back
// for the next chunk, dispatch on event:/data: line pairs, flush any trailing
// buffer once the stream ends). `: keepalive` comment frames the backend sends
// every 30s are skipped automatically — they match neither the `event: ` nor
// `data: ` prefixes below, same as generatePageHtml's loop.
export async function streamConciergeChat(
  conversationId: string,
  message: string,
  handlers: ConciergeStreamHandlers,
): Promise<void> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  const res = await fetch(`${API_BASE}/concierge/conversations/${conversationId}/chat`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ message }),
  });

  // 401-refresh-before-stream: single-flight refresh (shared with the rest of
  // api.ts), then retry the whole call once. The stream hasn't been consumed
  // yet at this point, so re-POSTing is safe (same as generatePageHtml).
  if (res.status === 401 && refreshToken) {
    const newAccess = await refreshOnce();
    if (newAccess) {
      return streamConciergeChat(conversationId, message, handlers);
    }
    // #2189 — refreshOnce() already ran the session-expired flow itself (generationally
    // gated — see handleSessionExpired); this request still fails either way.
    handlers.onError?.('Your session expired. Please sign in again.');
    return;
  }

  if (!res.ok) {
    try {
      const err = await res.json();
      handlers.onError?.(err.error || `Server error: ${res.status}`);
    } catch {
      handlers.onError?.(`Server error: ${res.status}`);
    }
    return;
  }

  if (!res.body) {
    handlers.onError?.('No response body from server');
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let eventType = '';

  const processLines = (lines: string[]) => {
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        eventType = line.slice(7);
      } else if (line.startsWith('data: ')) {
        const data = line.slice(6);
        if (eventType === 'token') {
          try { handlers.onToken?.(JSON.parse(data)); } catch { /* skip */ }
        } else if (eventType === 'tool_call') {
          try {
            const parsed = JSON.parse(data);
            handlers.onToolCall?.(parsed.name, parsed.input);
          } catch { /* skip */ }
        } else if (eventType === 'tool_result') {
          try {
            const parsed = JSON.parse(data);
            handlers.onToolResult?.(parsed.name, parsed.summary);
          } catch { /* skip */ }
        } else if (eventType === 'done') {
          try {
            const parsed = JSON.parse(data);
            handlers.onDone?.(parsed.text);
          } catch { /* skip */ }
        } else if (eventType === 'error') {
          try {
            const errData = JSON.parse(data);
            handlers.onError?.(errData.error || 'Unknown error');
          } catch {
            handlers.onError?.(data);
          }
        }
        eventType = '';
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    processLines(lines);
  }
  // Final buffer flush — the last chunk before stream-close may not end in a
  // trailing '\n\n', so anything left in `buffer` after the loop exits still
  // needs to be parsed (same as generatePageHtml/sendMessageStream).
  if (buffer.trim()) {
    processLines(buffer.split('\n'));
  }
}

// ── Partner Signup API ──

export interface PartnerRegisterData {
  email: string;
  password: string;
  companyName: string;
  contactName: string;
  phone?: string;
  website?: string;
  taxFormType: 'w9' | 'w8';
  taxData: Record<string, unknown>;
  paypalEmail: string;
  minPayoutCents: number;
  inviteToken?: string;
  partnerType?: 'reseller' | 'affiliate';
  // Phase 1: reseller-only white-label domain config (skipped for affiliates).
  resellerDomain?: {
    kind: 'purchased' | 'byo' | 'deferred';
    domain?: string | null;
    source?: string | null;
    // Phase 1.5: card-on-file when buying through us. Backend creates CIM
    // profile + charges before calling 20i registerDomain. Phase 0.1c: the
    // raw fields are now optional and the Accept.js opaque nonce fields are
    // added, so either shape typechecks (tokenizeCard falls back to raw
    // only when the public client key is not yet configured).
    card?: {
      cardNumber?: string;
      cardExpiry?: string;
      cardCvc?: string;
      cardDataDescriptor?: string;
      cardDataValue?: string;
      billingZip: string;
    };
  };
}

export async function partnerRegister(data: PartnerRegisterData): Promise<ApiResponse<{
  message: string;
  autoApproved: boolean;
  tokens?: { accessToken: string; refreshToken: string };
  /** Phase 1.6: present when reseller picked BYO — nameservers to publish. */
  byoSetup?: {
    domain: string;
    nameservers: string[];
  };
}>> {
  return request('/auth/partner-register', { method: 'POST', body: JSON.stringify(data) });
}

export async function updatePartnerTaxForm(payload: {
  taxFormType: 'w9' | 'w8';
  taxData: Record<string, unknown>;
}): Promise<ApiResponse<{ message: string }>> {
  return request('/partner/tax-forms', { method: 'PUT', body: JSON.stringify(payload) });
}

// ── DNS Management API ──

export async function getDnsRecords(projectId: string): Promise<ApiResponse<any[]>> {
  return request(`/projects/${projectId}/dns/records`);
}

export async function createDnsRecord(projectId: string, record: any): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/dns/records`, { method: 'POST', body: JSON.stringify(record) });
}

export async function updateDnsRecord(projectId: string, recordId: string, record: any): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/dns/records/${recordId}`, { method: 'PUT', body: JSON.stringify(record) });
}

export async function deleteDnsRecord(projectId: string, recordId: string): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/dns/records/${recordId}`, { method: 'DELETE' });
}

export async function captureDnsSnapshot(projectId: string): Promise<ApiResponse<any>> {
  return request(`/projects/${projectId}/dns/snapshot`, { method: 'POST' });
}

export async function getDnsSnapshot(projectId: string): Promise<ApiResponse<any>> {
  return request(`/projects/${projectId}/dns/snapshot`);
}

export async function importDnsSnapshot(projectId: string, selectedIndices?: number[]): Promise<ApiResponse<{ imported: number }>> {
  return request(`/projects/${projectId}/dns/import`, { method: 'POST', body: JSON.stringify({ selectedIndices }) });
}

export async function applyDnsPreset(
  projectId: string,
  presetSlug: string,
  tenant?: string,
  additionalSpfIncludes?: string[],
): Promise<ApiResponse<{ records: any[]; deleted?: number; spf?: string }>> {
  return request(`/projects/${projectId}/dns/apply-preset`, {
    method: 'POST',
    body: JSON.stringify({ presetSlug, tenant, additionalSpfIncludes }),
  });
}

export async function startSourcePrep(
  projectId: string,
  sourceUrl?: string,
): Promise<ApiResponse<{ runId: string; status: string; currentStep?: string }>> {
  return request(`/projects/${projectId}/prep-source`, {
    method: 'POST',
    body: JSON.stringify(sourceUrl ? { sourceUrl } : {}),
  });
}

// ── §15 Project Cards CRUD (SF_CARDS_CRUD) ──
// All functions call routes that 404 when the flag is off; callers check
// res.success === false and res.error to detect the flag-off state.

export interface ProjectCard {
  id: string;
  kind: string;
  title: string;
  excerpt: string | null;
  link_url: string | null;
  author: string | null;
  role: string | null;
  image_source_url: string | null;
  source_index: number;
  detail_html: string | null;
  detail_title: string | null;
  detail_meta_description: string | null;
  detail_image_url: string | null;
  created_at: string;
  // Phase 5 (#255) — per-page FAQs. Only meaningful for kind 'faq'; optional
  // because older rows/readers may still omit them.
  page_slug?: string;
  updated_at?: string;
}

export async function listCards(
  projectId: string,
  kind?: string,
): Promise<ApiResponse<ProjectCard[]>> {
  const qs = kind ? `?kind=${encodeURIComponent(kind)}` : '';
  return request(`/projects/${projectId}/cards${qs}`);
}

/** #293 — the card-backed sections the LIVE site rebuilds that depend on the project's cards: the pages whose FAQ is
 *  rendered from FAQ cards (#292) and whether real portfolio cards exist (#250). Testimonial / team / gallery are
 *  always rebuilt, so they need no answer from the server. #406: testimonialGuard = the publish keeps a band whose cards
 *  are not testimonials as designed (SF_TESTIMONIAL_SECTION_GUARD), so the editor must not lock such a band. */
export async function getCardDrivenState(projectId: string): Promise<ApiResponse<{ faqPages: string[]; portfolio: boolean; testimonialGuard?: boolean }>> {
  return request(`/projects/${projectId}/cards/driven`);
}

export async function createCard(
  projectId: string,
  data: Partial<Omit<ProjectCard, 'id' | 'source_index' | 'created_at'>>,
): Promise<ApiResponse<ProjectCard>> {
  return request(`/projects/${projectId}/cards`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCard(
  projectId: string,
  cardId: string,
  data: Partial<Omit<ProjectCard, 'id' | 'kind' | 'source_index' | 'created_at'>>,
): Promise<ApiResponse<ProjectCard>> {
  return request(`/projects/${projectId}/cards/${cardId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCard(
  projectId: string,
  cardId: string,
): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/cards/${cardId}`, { method: 'DELETE' });
}

export async function reorderCards(
  projectId: string,
  items: Array<{ cardId: string; source_index: number }>,
): Promise<ApiResponse<void>> {
  return request(`/projects/${projectId}/cards/reorder`, {
    method: 'PUT',
    body: JSON.stringify(items),
  });
}

export async function publishCards(
  projectId: string,
): Promise<ApiResponse<{ cardCount: number; action: string; message?: string }>> {
  return request(`/projects/${projectId}/cards/publish`, { method: 'POST' });
}

// #2230 (W2-N) — publishCards above answers "Publishing…" and runs the live-site update in the
// background; these let the dashboard learn whether it actually landed and retry if not.
export async function getCardsPublishStatus(projectId: string): Promise<ApiResponse<SyncOutcome>> {
  return request(`/projects/${projectId}/cards/publish-status`);
}

export async function retryCardsPublish(
  projectId: string,
): Promise<ApiResponse<{ cardCount: number; action: string; message?: string }>> {
  return request(`/projects/${projectId}/cards/publish/retry`, { method: 'POST' });
}

// ── Phase 5 (#255) — per-page FAQs, two-way GigaPress<->WordPress sync ──

export type FaqSyncTrigger = 'tab_open' | 'after_save';

export type FaqSyncStatus = 'synced' | 'unreachable' | 'skipped' | 'cooldown' | 'disabled';

export interface FaqSyncCounts {
  pulled: number;
  imported: number;
  updatedFromWp: number;
  deletedFromWp: number;
  restoredFromWp: number;
  gigapressWon: number;
  claimed: number;
}

export interface FaqSyncResult {
  status: FaqSyncStatus;
  lastSyncedAt: string | null;
  lastError: string | null;
  counts?: FaqSyncCounts;
}

export async function faqSync(
  projectId: string,
  trigger: FaqSyncTrigger,
): Promise<ApiResponse<FaqSyncResult>> {
  return request(`/projects/${projectId}/cards/faq-sync`, {
    method: 'POST',
    body: JSON.stringify({ trigger }),
  });
}

export interface FaqSyncStatusInfo {
  lastSyncedAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
}

export async function getFaqSyncStatus(projectId: string): Promise<ApiResponse<FaqSyncStatusInfo>> {
  return request(`/projects/${projectId}/cards/faq-sync-status`);
}

export async function getImportedContentSummary(projectId: string): Promise<ApiResponse<{
  postCount: number;
  productCount: number;
  testimonialCount: number;
  cptTypeCount: number;
  cptEntryCount: number;
  posts: Array<{ title: string; sourceUrl?: string }>;
  products: Array<{ name: string; price?: string }>;
  testimonials: Array<{ author: string; text: string }>;
  cptPosts: Record<string, Array<{ title: string }>>;
}>> {
  return request(`/projects/${projectId}/imported-content-summary`);
}

// ── Sales portal (Phase 2.2) ──
// Separate base path from the /admin/* functions above — sales.routes.ts is
// mounted at /api/sales so sales/sub_admin reps (who can't reach /api/admin)
// can use it too.

export interface SalesClientRecord {
  id: string;
  business_name: string;
  email: string;
  created_at: string;
  order_status: string | null;
  package_name: string | null;
}

export async function createSalesClient(data: {
  email: string;
  businessName: string;
  phone?: string;
  packageId?: string;
}): Promise<ApiResponse<{ clientId: string; userId: string; paymentLinkUrl?: string; paymentLinkError?: string }>> {
  return request('/sales/clients', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// REQUIRED: mint one UUID per charge-modal instance and reuse it across
// retries from that instance, same idempotency contract as adminChargeClient
// above (billing.service chargeAndRecord dedups on it).
export async function salesTerminalCharge(
  clientId: string,
  data: { amountCents?: number; addonSlug?: string; description?: string; idempotencyKey: string },
): Promise<ApiResponse<{ transactionId?: string; alreadyProcessed?: boolean }>> {
  return request(`/sales/terminal-charge/${clientId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function salesSendPaymentLink(
  clientId: string,
  data: { packageId: string; addonIds?: string[] },
): Promise<ApiResponse<{ url: string }>> {
  return request(`/sales/send-payment-link/${clientId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getSalesClients(): Promise<ApiResponse<SalesClientRecord[]>> {
  return request('/sales/clients');
}

export async function getSalesOutreach(): Promise<ApiResponse<{
  totalPreviews: number;
  readyPreviews: number;
  conversions: number;
  previewsLast7d: number;
  conversionsLast7d: number;
}>> {
  return request('/sales/outreach');
}

// ── Staff management (Phase 2.2, admin-only — mounted under /admin/staff-accounts) ──
// NOT "/admin/staff" — that path is already GET-only for getAdminStaff()
// above (admin/superadmin users, for the ticket-assignment dropdown). See
// the matching NOTE in admin.routes.ts for why this uses a different path
// rather than overloading that existing endpoint.

export interface StaffRecord {
  id: string;
  email: string;
  role: 'sales' | 'sub_admin' | 'va' | 'support';
  name: string | null;
  created_at: string;
  is_active: boolean;
}

export async function getStaff(): Promise<ApiResponse<StaffRecord[]>> {
  return request('/admin/staff-accounts');
}

export async function createStaff(data: {
  email: string;
  role: 'sales' | 'sub_admin' | 'va' | 'support';
  name: string;
}): Promise<ApiResponse<StaffRecord & { inviteEmailed?: boolean }>> {
  return request('/admin/staff-accounts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateStaff(
  id: string,
  data: { role?: 'sales' | 'sub_admin' | 'va' | 'support'; name?: string },
): Promise<ApiResponse<StaffRecord>> {
  return request(`/admin/staff-accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

// Deactivate (active:false) or reactivate (active:true) a staff account. The
// backend flips users.is_active and, on deactivate, purges refresh tokens so a
// live session is logged out; login rejects a disabled account with 403.
export async function setStaffActive(id: string, active: boolean): Promise<ApiResponse<StaffRecord>> {
  return request(`/admin/staff-accounts/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ active }),
  });
}

// Permanently delete a staff account (sales / sub_admin / va).
export async function deleteStaff(id: string): Promise<ApiResponse<{ deletedStaffId: string; deletedEmail: string }>> {
  return request(`/admin/staff-accounts/${id}`, { method: 'DELETE' });
}

// Re-send the set-password invite email to a staff member (mints a fresh link).
export async function resendStaffInvite(id: string): Promise<ApiResponse<{ inviteEmailed: boolean }>> {
  return request(`/admin/staff-accounts/${id}/resend-invite`, { method: 'POST' });
}

// Mints a fresh set-password link for a staff account and RETURNS it (no email) so
// an admin can copy + share it manually — e.g. the invite email bounced or was
// filtered. Each call supersedes the prior link.
export async function getStaffInviteLink(id: string): Promise<ApiResponse<{ inviteUrl: string }>> {
  return request(`/admin/staff-accounts/${id}/invite-link`, { method: 'POST' });
}
